import { useCallback, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, addDays, isBefore, parseISO, getDay } from "date-fns";
import { Order, OrderStatus, Tractor, Driver, Location } from "@/types/tms";
import { findLocationByAddress } from "@/lib/location-utils";
import { OrderFormData } from "@/components/orders/orderFormTypes";
import {
  legAdvancedPayload,
  orderAdvancedPayload,
} from "@/components/orders/form/advancedFields";
import { ensureTrailerExists } from "@/lib/trailer-utils";
import { ParsedTrip } from "@/components/orders/csvOrderParser";
import { getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { useTenant } from "@/hooks/useTenant";
import {
  mapOrderToCreateTrip,
  mapLegToCreateTrip,
  buildEapiDelta,
  assertTripBodyHasLatLng,
  diffCreateResponseForCorrection,
} from "@/lib/eapi-trip-mapper";
import type { EapiCreateTripBody } from "@/lib/eapi-trip-mapper";
import { isNoEapiState } from "@/lib/eapi-eligibility";
import { buildCustomFieldsForOrder } from "@/lib/eapi-custom-fields";
import { formLocalToUtc, utcToFormLocal, PLANNING_TZ } from "@/lib/timezone";
import { syncTrailerBol } from "@/lib/trailer-bol-sync";
import { removeTrailerBolFromOrder } from "@/lib/bol-trailer-unsync";
import { removeOrderBolsOnCancel } from "@/lib/bol-cancellation-cleanup";
import {
  syncOrderToLegs,
  syncOrderLegsAndTrip,
  listActiveLegTripsForOrder,
  supersedeLegTrip,
  fetchLegRow,
  insertActiveLegTrip,
  touchActiveLegTrip,
} from "@/lib/leg-trip-sync";
import { resolveSingleLegTripId } from "@/lib/leg-one-trip";
import { cancelOtherActiveLegTrips } from "@/lib/eapi-leg-cancel";
import { isAlreadyCancelledUpstreamError } from "@/lib/eapi-cancel-utils";
import { getTractorDoubleBookingMessage } from "@/lib/order-conflict-errors";
import { notifyOrderStatus } from "@/lib/notify-order-status";
import { resolveEditStatus, safeOrderStatus } from "@/lib/order-status";
import {
  syncPerLegEapiTrips,
  describeUnlinkedTrips,
  type UnlinkedReplacementTrip,
} from "@/lib/per-leg-trip-sync";
import { retireOrphanedOrderLevelTrip } from "@/lib/order-level-trip-orphan";
import { legMirrorsParentEquipment } from "@/lib/confirm-plan-legs";
import { legDriverStatusForOrderEdit } from "@/lib/multi-leg-confirm-status";

import { createTripsForLegs, type PromoteTripRequest } from "@/lib/promote-order-trips";
import { createEapiTripChecked, correctStaleCreateResponse } from "@/lib/eapi-create-trip";
import { resolveChangedParentAssetMirror, resolveParentAssetMirror } from "@/lib/parent-asset-mirror";
import { diffUpdate } from "@/lib/order-update-diff";
import { fanOutDeliveredToLegs } from "@/lib/deliver-legs-fanout";
import { settleTrailerAfterOrderClose } from "@/lib/trailer-settle";
import { OPEN_ENDED_TEMPLATE_END_DATE } from "@/lib/recurringTemplateForm";
import { fetchMissingConfirmDocs } from "@/hooks/useConfirmDocGate";
import { buildConfirmDocsOverrideEvent } from "@/lib/confirm-docs-override-event";
import {
  fetchTrailerLoadStatuses,
  trailerNumbersForForm,
} from "@/hooks/usePendingBolEligibility";

/**
 * "Pending BOL" is a multi-leg-only waiver — never persist it from a
 * single-leg submit, whatever the payload says.
 */
function pendingBolValue(data: { order_type?: string; pending_bol?: boolean }): boolean {
  return data.order_type === "multi-leg" && data.pending_bol === true;
}
import { recordChangeEvents } from "@/hooks/useChangeEventPrompt";
import { useUserRole } from "@/hooks/useUserRole";
import {
  confirmDocsErrorMessage,
  formatMissingDocs,
  parseConfirmDocsError,
} from "@/lib/confirm-doc-gating";
import { missingConfirmRequirements } from "@/lib/confirm-readiness";
import { invalidateOrderQueries } from "@/lib/order-cache-keys";

/**
 * FK-is-authoritative reconciliation.
 *
 * When `ship_*_location_id` is set on the form payload, overwrite the matching
 * `ship_*_street_address` / `_city` / `_province_state` / `_postal_zip_code` /
 * `_country` fields from the linked `locations` row. Prevents the drift class
 * we found on AOP-HTSH-04411, where the FK pointed to one site (ELP1) while
 * the address text described another (Aledo / FTW1). Applied at the top of
 * both mutationFns so every write path (single-leg / multi-leg parent / per-
 * leg / recurring / bulk) inherits the same invariant — including the legs
 * array.
 *
 * Silent auto-correct (no toast). The FK is set via `LocationPicker`, which
 * is the user's expressed intent; the address text is a derived display field
 * that can legitimately go stale (e.g. when a location row is renamed).
 */
function applyLocationFromFK<T extends Record<string, any>>(
  obj: T,
  prefix: "ship_from" | "ship_to",
  locations: Location[] | undefined,
): T {
  const id = obj[`${prefix}_location_id`];
  if (!id || !locations) return obj;
  const loc = locations.find((l) => l.id === id);
  if (!loc) return obj;
  return {
    ...obj,
    [`${prefix}_street_address`]: loc.street_address || "",
    [`${prefix}_city`]: loc.city || "",
    [`${prefix}_province_state`]: loc.province_state || "",
    [`${prefix}_postal_zip_code`]: loc.postal_zip_code || "",
    [`${prefix}_country`]: loc.country || "",
  };
}

function reconcileOrderFormFromFK(
  data: OrderFormData,
  locations: Location[] | undefined,
): OrderFormData {
  if (!locations) return data;
  let next: OrderFormData = applyLocationFromFK(
    applyLocationFromFK(data, "ship_from", locations),
    "ship_to",
    locations,
  );
  if (Array.isArray(next.legs) && next.legs.length > 0) {
    next = {
      ...next,
      legs: next.legs.map((leg) =>
        applyLocationFromFK(
          applyLocationFromFK(leg, "ship_from", locations),
          "ship_to",
          locations,
        ),
      ),
    };
  }
  return next;
}

/** Best-effort wrapper around `ensureTrailerExists` so create-path failures
 *  never block order creation. Matches the update-path behavior (log only). */
async function safeEnsureTrailerExists(args: {
  trailerId: string;
  trailerTypeId?: string | null;
  shipFromCity?: string | null;
  tenantId?: string | null;
}) {
  try {
    await ensureTrailerExists(args);
  } catch (e) {
    console.error("Failed to auto-provision trailer on order create:", e);
  }
}

// `correctStaleCreateResponse` + `createEapiTripChecked` live in
// `@/lib/eapi-create-trip` so the confirmation flow shares one implementation.

interface UseOrderMutationsParams {
  userEmail: string | null | undefined;
  tractors: Tractor[] | undefined;
  drivers: Driver[] | undefined;
  locations?: Location[] | undefined;
  onSuccess: () => void;
  onCreated?: (orderId: string) => void;
  /** Fired after a recurring-template-backed create. Used to navigate to
   *  the template detail page from the Create New Order flow. */
  onTemplateCreated?: (templateId: string) => void;
}

// `cancelOtherActiveLegTrips` extracted to `@/lib/eapi-leg-cancel` so other
// cancel paths (e.g. `useOrderCancellation`) can reuse it without depending on
// this hook's internals.


const EAPI_READY_WAIT_MS = 15000;
const EAPI_READY_POLL_MS = 250;

export function useOrderMutations({ userEmail, tractors, drivers, locations, onSuccess, onCreated, onTemplateCreated }: UseOrderMutationsParams) {
  const queryClient = useQueryClient();
  const { eapiClient, isReady: eapiReady, environment: eapiEnvironment } = useEapiAuth();
  const { tenantId, activeTenant } = useTenant();
  // Admins may confirm an order with missing billing documentation after
  // acknowledging the warning surfaced by the confirm UI.
  const { isAdmin } = useUserRole();

  // Keep refs in sync so mutation callbacks always read the latest values
  const eapiClientRef = useRef(eapiClient);
  const eapiReadyRef = useRef(eapiReady);
  useEffect(() => { eapiClientRef.current = eapiClient; }, [eapiClient]);
  useEffect(() => { eapiReadyRef.current = eapiReady; }, [eapiReady]);
  const eapiEnvironmentRef = useRef(eapiEnvironment);
  useEffect(() => { eapiEnvironmentRef.current = eapiEnvironment; }, [eapiEnvironment]);

  /** Ensure the PostgREST session has the correct tenant context for RLS evaluation */
  const ensureTenantContext = useCallback(async () => {
    await supabase.rpc("set_tenant_slug", { _slug: activeTenant });
  }, [activeTenant]);

  const waitForEapiClient = useCallback(async () => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < EAPI_READY_WAIT_MS) {
      const client = eapiClientRef.current;
      const ready = eapiReadyRef.current;

      if (client && ready) {
        return client;
      }

      await new Promise((resolve) => window.setTimeout(resolve, EAPI_READY_POLL_MS));
    }

    console.warn("EAPI auth did not become ready in time for tenant", activeTenant);
    return null;
  }, [activeTenant]);

  // Note: EAPI retry/dismiss state was used by the old "create-then-sync" flow,
  // where the local order existed without an external trip ID. The new EAPI-first
  // create flow eliminates that scenario, so the retry banner is no longer needed.

  const createMutation = useMutation({
    mutationFn: async (data: OrderFormData) => {
      await ensureTenantContext();
      // FK is authoritative for address fields — see `reconcileOrderFormFromFK`.
      data = reconcileOrderFormFromFK(data, locations);

      // ── Recurring takes precedence over multi-leg ──
      // Previously the multi-leg branch ran first, which meant a recurring +
      // multi-leg submission silently became a single one-off multi-leg
      // order with no template ever written. We now branch on `is_recurring`
      // first so both single-leg and multi-leg recurring orders flow through
      // the template model.
      const isRecurringSubmit =
        data.is_recurring &&
        data.recurrence_days.length > 0;
      const isMultiLeg = data.order_type === "multi-leg" && data.legs.length >= 2;

      // ── Pre-flight: billing documentation gate (Document Confirm Gating) ──
      // A brand-new order created directly as `confirmed`
      // has no BOL/Tender images yet, so it can only clear the gate when the
      // billing party requires nothing beyond what the form supplies.
      if (
        !isAdmin &&
        data.status === "confirmed" &&
        data.billing_party_id
      ) {
        const { missing } = await fetchMissingConfirmDocs({
          orderId: null,
          billingPartyId: data.billing_party_id,
          tenderId: data.tender_id ?? null,
          tenantId,
          pendingBol: pendingBolValue(data),
          legTrailerLoadStatuses: await fetchTrailerLoadStatuses(
            trailerNumbersForForm(data),
            tenantId,
          ),
        });
        if (missing.length > 0) throw new Error(confirmDocsErrorMessage(missing));
      }

      // Multi-leg, non-recurring: parent + child rows
      if (isMultiLeg && !isRecurringSubmit) {
        // Guard: if the locations cache hasn't loaded yet, the client-side
        // address→location_id resolution silently returns null and we'd
        // persist null FKs on every leg. A DB trigger now backfills these
        // server-side, but we still surface a clearer failure mode here so
        // the operator isn't left with un-linked legs if the trigger ever
        // can't resolve (e.g. brand-new address).
        if (!locations) {
          throw new Error(
            "Locations are still loading. Please wait a moment and try again.",
          );
        }
        const legs = data.legs;
        const lastLeg = legs[legs.length - 1];

        const parentData = {
          status: "booked" as OrderStatus,
          billing_party_id: data.billing_party_id,
          // Parent asset fields are a mirror of leg 1 when assets are per-leg.
          // Never write the order-level form values directly, or a cleared
          // leg leaves a stale parent assignment behind (false "booked").
          ...resolveParentAssetMirror(data),
          tender_id: data.tender_id || null,
          trip_type: data.trip_type || null,
          pending_bol: pendingBolValue(data),
          ship_from_street_address: legs[0].ship_from_street_address,
          ship_from_city: legs[0].ship_from_city,
          ship_from_province_state: legs[0].ship_from_province_state || null,
          ship_from_postal_zip_code: legs[0].ship_from_postal_zip_code || null,
          ship_from_country: legs[0].ship_from_country || null,
          ship_to_street_address: lastLeg.ship_to_street_address,
          ship_to_city: lastLeg.ship_to_city,
          ship_to_province_state: lastLeg.ship_to_province_state || null,
          ship_to_postal_zip_code: lastLeg.ship_to_postal_zip_code || null,
          ship_to_country: lastLeg.ship_to_country || null,
          ready_time: formLocalToUtc(legs[0].ready_time),
          requested_delivery_time: formLocalToUtc(lastLeg.requested_delivery_time),
          ready_time_end: formLocalToUtc(legs[0].ready_time_end || legs[0].ready_time),
          requested_delivery_time_end: formLocalToUtc(lastLeg.requested_delivery_time_end || lastLeg.requested_delivery_time),
          parent_order_id: null,
          leg_sequence: 0,
          unique_id: "",
          created_by: userEmail || null,
          ...(tenantId ? { tenant_id: tenantId } : {}),
          // Advanced ("Order API parity") fields. Shuttle carriers mirror the
          // first/last leg so the stitched parent header stays coherent.
          ...orderAdvancedPayload({
            origin_shuttle_carrier_name: legs[0].origin_shuttle_carrier_name,
            origin_shuttle_carrier_dot: legs[0].origin_shuttle_carrier_dot,
            destination_shuttle_carrier_name: lastLeg.destination_shuttle_carrier_name,
            destination_shuttle_carrier_dot: lastLeg.destination_shuttle_carrier_dot,
            freight_weight_lbs: data.freight_weight_lbs,
            commodity_code: data.commodity_code,
            commodity_description: data.commodity_description,
          }),
        };

        const { data: parentId, error: parentError } = await supabase.rpc("insert_order", {
          _data: parentData,
        });
        if (parentError) throw parentError;
        const parent = { id: parentId as string };

        // Auto-provision a trailers row if a trailer_id was assigned on create.
        if (data.trailer_id) {
          await safeEnsureTrailerExists({
            trailerId: data.trailer_id,
            trailerTypeId: data.trailer_type_id,
            shipFromCity: legs[0].ship_from_city,
            tenantId,
          });
        }

        // Phase 5: write legs directly into public.legs. We no longer create
        // child rows in `orders`, so no extra unique_ids are burned.
        if (!tenantId) throw new Error("No active tenant");
        const useSameAssets = data.use_same_assets_for_all_legs !== false;
        const useSameTripType = data.use_same_trip_type_for_all_legs !== false;
        const legRows = legs.map((leg, i) => ({
          order_id: parent.id,
          tenant_id: tenantId,
          leg_sequence: i + 1,
          assigned_tractor_id:
            (useSameAssets ? data.assigned_tractor_id : leg.assigned_tractor_id) || null,
          assigned_operator_id:
            (useSameAssets ? data.assigned_operator_id : leg.assigned_operator_id) || null,
          assigned_operator_2_id:
            (useSameAssets ? data.assigned_operator_2_id : leg.assigned_operator_2_id) || null,
          trailer_type_id:
            (useSameAssets ? data.trailer_type_id : leg.trailer_type_id) || null,
          // Phase 1 per-leg trailer: honor leg.trailer_id when per-leg assets
          // is on; fan out order-level trailer_id when the toggle is off.
          trailer_id:
            (useSameAssets ? data.trailer_id : leg.trailer_id) || null,
          trip_type:
            (useSameTripType ? null : (leg.trip_type || null)) as any,
          ship_from_street_address: leg.ship_from_street_address,
          ship_from_city: leg.ship_from_city,
          ship_from_province_state: leg.ship_from_province_state || null,
          ship_from_postal_zip_code: leg.ship_from_postal_zip_code || null,
          ship_from_country: leg.ship_from_country || null,
          ship_to_street_address: leg.ship_to_street_address,
          ship_to_city: leg.ship_to_city,
          ship_to_province_state: leg.ship_to_province_state || null,
          ship_to_postal_zip_code: leg.ship_to_postal_zip_code || null,
          ship_to_country: leg.ship_to_country || null,
          ship_from_location_id:
            (leg as any).ship_from_location_id ||
            findLocationByAddress(locations, leg.ship_from_street_address, leg.ship_from_city)?.id ||
            null,
          ship_to_location_id:
            (leg as any).ship_to_location_id ||
            findLocationByAddress(locations, leg.ship_to_street_address, leg.ship_to_city)?.id ||
            null,
          ready_time: formLocalToUtc(leg.ready_time),
          requested_delivery_time: formLocalToUtc(leg.requested_delivery_time),
          ready_time_end: formLocalToUtc(leg.ready_time_end || leg.ready_time),
          requested_delivery_time_end: formLocalToUtc(leg.requested_delivery_time_end || leg.requested_delivery_time),
          ...legAdvancedPayload(leg),
        }));

        const { error: legsError } = await supabase.from("legs").insert(legRows);
        if (legsError) throw legsError;
        return { kind: "multi-leg" as const, count: legs.length, orderId: parent.id };
      }

      // Recurring orders (single-leg or multi-leg). Multi-leg writes a
      // template + `recurring_order_template_legs` and delegates occurrence
      // materialization to the `generate-recurring-orders` edge function.
      if (isRecurringSubmit) {
        if (!tenantId) throw new Error("No active tenant");
        const multiLegRecurring = isMultiLeg;
        const firstLeg = multiLegRecurring ? data.legs[0] : null;
        const lastLeg = multiLegRecurring ? data.legs[data.legs.length - 1] : null;
        // Source the form-level addresses + times from the first/last leg so
        // the rest of this branch (template insert, occurrence dedup, etc.)
        // sees a coherent representation of the trip end-to-end.
        if (multiLegRecurring && firstLeg && lastLeg) {
          data = {
            ...data,
            ship_from_location_id: (firstLeg as any).ship_from_location_id || "",
            ship_from_street_address: firstLeg.ship_from_street_address,
            ship_from_city: firstLeg.ship_from_city,
            ship_from_province_state: firstLeg.ship_from_province_state,
            ship_from_postal_zip_code: firstLeg.ship_from_postal_zip_code,
            ship_from_country: firstLeg.ship_from_country,
            ship_to_location_id: (lastLeg as any).ship_to_location_id || "",
            ship_to_street_address: lastLeg.ship_to_street_address,
            ship_to_city: lastLeg.ship_to_city,
            ship_to_province_state: lastLeg.ship_to_province_state,
            ship_to_postal_zip_code: lastLeg.ship_to_postal_zip_code,
            ship_to_country: lastLeg.ship_to_country,
            ready_time: firstLeg.ready_time,
            ready_time_end: firstLeg.ready_time_end,
            requested_delivery_time: lastLeg.requested_delivery_time,
            requested_delivery_time_end: lastLeg.requested_delivery_time_end,
          };
        }
        const startDate = data.ready_time ? parseISO(data.ready_time) : new Date();
        // Open-ended recurrences (no end date on the form) are stored with the
        // shared `2099-12-31` sentinel so the NOT NULL column and the nightly
        // generator keep working unchanged.
        const endDate = data.recurrence_end_date
          ? parseISO(data.recurrence_end_date)
          : parseISO(OPEN_ENDED_TEMPLATE_END_DATE);
        const ordersToCreate: any[] = [];
        const recurrenceGroupId = crypto.randomUUID();
        // `readyTime` is only used for its wall-clock hour/minute (parseISO
        // reads the naive form string as browser-local, and getHours() reads
        // it back the same way, so the typed digits survive). Durations are
        // measured between real Central instants so they can't be skewed by
        // the browser's own DST transitions.
        const readyTime = parseISO(data.ready_time);
        const msOf = (local: string | null | undefined) => {
          const utc = formLocalToUtc(local);
          return utc ? new Date(utc).getTime() : null;
        };
        const readyMs = msOf(data.ready_time) ?? readyTime.getTime();
        const deliveryMs = msOf(data.requested_delivery_time) ?? readyMs;
        const timeDiffMs = deliveryMs - readyMs;
        const readyEndOffsetMs = Math.max(0, (msOf(data.ready_time_end) ?? readyMs) - readyMs);
        const deliveryEndOffsetMs = Math.max(0, (msOf(data.requested_delivery_time_end) ?? deliveryMs) - deliveryMs);

        // ── Template-first model ──
        let templateId: string | null = null;
        // Insert a `recurring_order_templates` row that mirrors the order
        // form fields. Every concrete order generated here (and by the
        // nightly cron going forward) is linked to it. Both the Customers
        // create flow and this one converge on the same source of truth.
        const departureHH = String(readyTime.getHours()).padStart(2, "0");
        const departureMM = String(readyTime.getMinutes()).padStart(2, "0");
        const tripDurationMinutes = Math.max(1, Math.round(timeDiffMs / 60000));
        const startDateISO = format(startDate, "yyyy-MM-dd");
        const endDateISO = format(endDate, "yyyy-MM-dd");
        const templateUniqueId = `REC-${recurrenceGroupId.slice(0, 8).toUpperCase()}`;
        const templateInsert: Record<string, unknown> = {
          unique_id: templateUniqueId,
          billing_party_id: data.billing_party_id,
          ship_from_location_id: data.ship_from_location_id || null,
          ship_to_location_id: data.ship_to_location_id || null,
          ship_from_street_address: data.ship_from_street_address,
          ship_from_street_address_2: (data as any).ship_from_street_address_2 || null,
          ship_from_address_name: (data as any).ship_from_address_name || null,
          ship_from_city: data.ship_from_city,
          ship_from_province_state: data.ship_from_province_state || null,
          ship_from_postal_zip_code: data.ship_from_postal_zip_code || null,
          ship_from_country: data.ship_from_country || null,
          ship_to_street_address: data.ship_to_street_address,
          ship_to_street_address_2: (data as any).ship_to_street_address_2 || null,
          ship_to_address_name: (data as any).ship_to_address_name || null,
          ship_to_city: data.ship_to_city,
          ship_to_province_state: data.ship_to_province_state || null,
          ship_to_postal_zip_code: data.ship_to_postal_zip_code || null,
          ship_to_country: data.ship_to_country || null,
          departure_time: `${departureHH}:${departureMM}`,
          // Typed in Central, so the nightly generator reads it as Central.
          timezone: PLANNING_TZ,
          trip_duration_minutes: tripDurationMinutes,
          recurrence_days: data.recurrence_days,
          start_date: startDateISO,
          end_date: endDateISO,
          ready_window_minutes: Math.max(0, Math.round(readyEndOffsetMs / 60000)),
          delivery_window_minutes: Math.max(0, Math.round(deliveryEndOffsetMs / 60000)),
          trailer_type_id: data.trailer_type_id || null,
          trip_type: data.trip_type || null,
          tender_id: data.tender_id || null,
          notes: (data as any).notes || null,
          bol_number: (data as any).bol_number || null,
          commodity_code: (data as any).commodity_code || null,
          commodity_description: (data as any).commodity_description || null,
          // Numeric column — an empty string from the advanced-fields input is
          // rejected by Postgres (22P02), so normalize blanks to NULL.
          freight_weight_lbs:
            (data as any).freight_weight_lbs === "" ||
            (data as any).freight_weight_lbs === undefined
              ? null
              : (data as any).freight_weight_lbs,
          customer_dot_number: (data as any).customer_dot_number || null,
          assigned_tractor_id: data.assigned_tractor_id || null,
          assigned_operator_id: data.assigned_operator_id || null,
          assigned_operator_2_id: (data as any).assigned_operator_2_id || null,
          trailer_id: data.trailer_id || null,
          origin_shuttle_carrier_name: (data as any).origin_shuttle_carrier_name || null,
          origin_shuttle_carrier_dot: (data as any).origin_shuttle_carrier_dot || null,
          destination_shuttle_carrier_name: (data as any).destination_shuttle_carrier_name || null,
          destination_shuttle_carrier_dot: (data as any).destination_shuttle_carrier_dot || null,
          order_type: multiLegRecurring ? "multi-leg" : "single-leg",
          created_by: userEmail || null,
          tenant_id: tenantId,
        };
        const { data: templateRow, error: tplInsertErr } = await supabase
          .from("recurring_order_templates")
          .insert(templateInsert as any)
          .select("id")
          .single();
        if (tplInsertErr) throw tplInsertErr;
        templateId = templateRow?.id as string;

        // Multi-leg: persist the per-leg breakdown so the generator can
        // materialize concrete legs for each occurrence.
        if (multiLegRecurring) {
          const useSameAssets = data.use_same_assets_for_all_legs !== false;
          const useSameTripType = data.use_same_trip_type_for_all_legs !== false;
          const baseReadyMs = parseISO(data.legs[0].ready_time).getTime();
          const templateLegRows = data.legs.map((leg, i) => {
            const legReadyMs = parseISO(leg.ready_time).getTime();
            const legDeliveryMs = parseISO(leg.requested_delivery_time).getTime();
            const legReadyEndMs = leg.ready_time_end
              ? parseISO(leg.ready_time_end).getTime()
              : legReadyMs;
            const legDeliveryEndMs = leg.requested_delivery_time_end
              ? parseISO(leg.requested_delivery_time_end).getTime()
              : legDeliveryMs;
            return {
              template_id: templateId,
              tenant_id: tenantId,
              leg_sequence: i + 1,
              ship_from_location_id:
                (leg as any).ship_from_location_id ||
                findLocationByAddress(locations, leg.ship_from_street_address, leg.ship_from_city)?.id ||
                null,
              ship_from_street_address: leg.ship_from_street_address,
              ship_from_city: leg.ship_from_city,
              ship_from_province_state: leg.ship_from_province_state || null,
              ship_from_postal_zip_code: leg.ship_from_postal_zip_code || null,
              ship_from_country: leg.ship_from_country || null,
              ship_to_location_id:
                (leg as any).ship_to_location_id ||
                findLocationByAddress(locations, leg.ship_to_street_address, leg.ship_to_city)?.id ||
                null,
              ship_to_street_address: leg.ship_to_street_address,
              ship_to_city: leg.ship_to_city,
              ship_to_province_state: leg.ship_to_province_state || null,
              ship_to_postal_zip_code: leg.ship_to_postal_zip_code || null,
              ship_to_country: leg.ship_to_country || null,
              start_offset_minutes: Math.max(0, Math.round((legReadyMs - baseReadyMs) / 60000)),
              trip_duration_minutes: Math.max(1, Math.round((legDeliveryMs - legReadyMs) / 60000)),
              ready_window_minutes: Math.max(0, Math.round((legReadyEndMs - legReadyMs) / 60000)),
              delivery_window_minutes: Math.max(0, Math.round((legDeliveryEndMs - legDeliveryMs) / 60000)),
              trip_type: (useSameTripType ? (data.trip_type || null) : (leg.trip_type || null)) as any,
              trailer_type_id:
                (useSameAssets ? data.trailer_type_id : leg.trailer_type_id) || null,
              // Phase 1 per-leg trailer on recurring template legs.
              trailer_id:
                (useSameAssets ? data.trailer_id : (leg.trailer_id || data.trailer_id)) || null,
              assigned_tractor_id:
                (useSameAssets ? data.assigned_tractor_id : leg.assigned_tractor_id) || null,
              assigned_operator_id:
                (useSameAssets ? data.assigned_operator_id : leg.assigned_operator_id) || null,
              assigned_operator_2_id:
                (useSameAssets ? data.assigned_operator_2_id : leg.assigned_operator_2_id) || null,
            };
          });
          const { error: tplLegsErr } = await supabase
            .from("recurring_order_template_legs")
            .insert(templateLegRows as any);
          if (tplLegsErr) throw tplLegsErr;

          // Skip the inline single-leg occurrence materialization below — the
          // generator edge function (invoked from onSuccess via
          // `trigger-template-generation`) will materialize multi-leg
          // occurrences using the template_legs we just inserted.
          if (data.trailer_id) {
            await safeEnsureTrailerExists({
              trailerId: data.trailer_id,
              trailerTypeId: data.trailer_type_id,
              shipFromCity: data.ship_from_city,
              tenantId,
            });
          }
          return {
            kind: "recurring" as const,
            count: 0,
            recurrenceGroupId,
            templateId,
          };
        }

        const createOrderForDate = (date: Date) => {
          // The typed time on this calendar date, in Central — never the
          // browser's timezone (previously setHours() + toISOString() made
          // occurrences depend on where the dispatcher's laptop was).
          const occurrenceLocal = `${format(date, "yyyy-MM-dd")}T${format(readyTime, "HH:mm")}`;
          const occurrenceReadyTime = new Date(formLocalToUtc(occurrenceLocal) ?? date.toISOString());
          const occurrenceDeliveryTime = new Date(occurrenceReadyTime.getTime() + timeDiffMs);
          const occurrenceReadyEnd = new Date(occurrenceReadyTime.getTime() + readyEndOffsetMs);
          const occurrenceDeliveryEnd = new Date(occurrenceDeliveryTime.getTime() + deliveryEndOffsetMs);
          return {
            status: "booked" as OrderStatus,
            ship_from_street_address: data.ship_from_street_address,
            ship_from_city: data.ship_from_city,
            ship_from_province_state: data.ship_from_province_state || null,
            ship_from_postal_zip_code: data.ship_from_postal_zip_code || null,
            ship_from_country: data.ship_from_country || null,
            ship_to_street_address: data.ship_to_street_address,
            ship_to_city: data.ship_to_city,
            ship_to_province_state: data.ship_to_province_state || null,
            ship_to_postal_zip_code: data.ship_to_postal_zip_code || null,
            ship_to_country: data.ship_to_country || null,
            ready_time: occurrenceReadyTime.toISOString(),
            requested_delivery_time: occurrenceDeliveryTime.toISOString(),
            ready_time_end: occurrenceReadyEnd.toISOString(),
            requested_delivery_time_end: occurrenceDeliveryEnd.toISOString(),
            billing_party_id: data.billing_party_id,
            unique_id: "",
            is_recurring: true,
            recurrence_days: data.recurrence_days,
            recurrence_end_date: data.recurrence_end_date || OPEN_ENDED_TEMPLATE_END_DATE,
            recurrence_group_id: recurrenceGroupId,
            recurring_order_template_id: templateId,
            created_by: userEmail || null,
            ...(tenantId ? { tenant_id: tenantId } : {}),
          };
        };

        ordersToCreate.push(createOrderForDate(startDate));
        // Only materialize a bounded horizon inline — the nightly
        // `generate-recurring-orders` cron keeps the rolling window topped up.
        // Without this cap an open-ended template (end date 2099) would try to
        // insert tens of thousands of occurrences at create time.
        const inlineHorizonEnd = addDays(startDate, 7);
        const lastDate = isBefore(inlineHorizonEnd, endDate) ? inlineHorizonEnd : endDate;
        let currentDate = addDays(startDate, 1);
        while (isBefore(currentDate, lastDate) || format(currentDate, 'yyyy-MM-dd') === format(lastDate, 'yyyy-MM-dd')) {
          const dayOfWeek = getDay(currentDate);
          if (data.recurrence_days.includes(dayOfWeek)) {
            ordersToCreate.push(createOrderForDate(currentDate));
          }
          currentDate = addDays(currentDate, 1);
        }

        const { data: batchCount, error } = await supabase.rpc("insert_orders_batch", {
          _orders: ordersToCreate,
        });
        if (error) throw error;
        // Auto-provision a trailers row once for the whole recurrence series.
        if (data.trailer_id) {
          await safeEnsureTrailerExists({
            trailerId: data.trailer_id,
            trailerTypeId: data.trailer_type_id,
            shipFromCity: data.ship_from_city,
            tenantId,
          });
        }
        return {
          kind: "recurring" as const,
          count: ordersToCreate.length,
          recurrenceGroupId,
          templateId,
        };
      } else {
        // Single-leg, single order — local-only create (no EAPI sync)
        // Orders are always created in `booked` status. EAPI `createTrip`
        // fires later via the update flow when the order is promoted to `confirmed`.
        if (!tenantId) throw new Error("No active tenant");
        const { data: reservedUniqueId, error: reserveErr } = await supabase
          .rpc("reserve_order_unique_id", {
            _billing_party_id: data.billing_party_id,
            _tenant_id: tenantId,
          });
        if (reserveErr) throw reserveErr;
        if (!reservedUniqueId) throw new Error("Failed to reserve order ID");

        const insertData = {
          // Always create as `booked`. Promotion to `confirmed` (and the EAPI
          // POST) is a deliberate, manual step handled by the update flow.
          status: "booked" as OrderStatus,
          ship_from_street_address: data.ship_from_street_address,
          ship_from_city: data.ship_from_city,
          ship_from_province_state: data.ship_from_province_state || null,
          ship_from_postal_zip_code: data.ship_from_postal_zip_code || null,
          ship_from_country: data.ship_from_country || null,
          ship_to_street_address: data.ship_to_street_address,
          ship_to_city: data.ship_to_city,
          ship_to_province_state: data.ship_to_province_state || null,
          ship_to_postal_zip_code: data.ship_to_postal_zip_code || null,
          ship_to_country: data.ship_to_country || null,
          ship_from_location_id:
            (data as any).ship_from_location_id ||
            findLocationByAddress(locations, data.ship_from_street_address, data.ship_from_city)?.id ||
            null,
          ship_to_location_id:
            (data as any).ship_to_location_id ||
            findLocationByAddress(locations, data.ship_to_street_address, data.ship_to_city)?.id ||
            null,
          ready_time: formLocalToUtc(data.ready_time),
          requested_delivery_time: formLocalToUtc(data.requested_delivery_time),
          ready_time_end: formLocalToUtc(data.ready_time_end || data.ready_time),
          requested_delivery_time_end: formLocalToUtc(data.requested_delivery_time_end || data.requested_delivery_time),
          billing_party_id: data.billing_party_id,
          unique_id: reservedUniqueId,
          eapi_trip_id: null,
          trailer_id: data.trailer_id || null,
          trailer_type_id: data.trailer_type_id || null,
          tender_id: data.tender_id || null,
          trip_type: data.trip_type || null,
          pending_bol: pendingBolValue(data),
          assigned_tractor_id: data.assigned_tractor_id || null,
          assigned_operator_id: data.assigned_operator_id || null,
          assigned_operator_2_id: data.assigned_operator_2_id || null,
          created_by: userEmail || null,
          tenant_id: tenantId,
          ...orderAdvancedPayload(data),
        };

        const { data: newId, error } = await supabase.rpc("insert_order", {
          _data: insertData,
        });
        if (error) throw error;

        // Phase 3 dual-write: mirror the single-leg order into `legs`.
        await syncOrderToLegs(newId as string);

        // Advanced leg-only columns (load status / temperature) aren't part of
        // `orders`, so apply them to the mirrored leg after the sync.
        {
          // `legs.trailer_id` is authoritative; write it explicitly.
          const legPayload = { ...legAdvancedPayload(data), trailer_id: data.trailer_id || null };
          await supabase
            .from("legs")
            .update(legPayload as any)
            .eq("order_id", newId as string)
            .eq("leg_sequence", 1);
        }

        // Auto-provision a trailers row if a trailer_id was assigned on create.
        if (data.trailer_id) {
          await safeEnsureTrailerExists({
            trailerId: data.trailer_id,
            trailerTypeId: data.trailer_type_id,
            shipFromCity: data.ship_from_city,
            tenantId,
          });
        }

        return { kind: "single" as const, count: 1, orderId: newId as string };
      }
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
      // Booked-truck lookup reads orders + legs; refresh it so a pivot
      // shows up immediately in other orders' tractor dropdowns.
      queryClient.invalidateQueries({ queryKey: ["tractor-conflict-ids"] });
      // NOTE: no confirm-docs override logging on the create path. Orders are
      // always created as `booked` (the Status field only renders when editing),
      // so `_docsOverride` can only originate from the Edit dialog, which logs
      // it in the update mutation.

      if (result?.orderId && tenantId && pendingBolValue(variables as any)) {
        void recordChangeEvents([
          {
            orderId: result.orderId,
            tenantId,
            eventType: "pending_bol_enabled",
            reason: "documentation_override",
            previousValue: { pending_bol: false },
            newValue: { pending_bol: true },
          },
        ]).then((r) => {
          if (r.error) console.warn("[useOrderMutations] pending-bol log failed", r.error);
        });
      }
      if (result.kind === "recurring") {
        toast.success(`Created ${result.count} recurring orders`);
      } else if (result.kind === "multi-leg") {
        toast.success(`Created multi-leg order with ${result.count} legs`);
      } else {
        toast.success("Order created successfully");
      }
      // If the newly created order has a trailer assigned, sync any
      // trailer-sourced BoLs (latest check-in BoL → S3 + order_bol_images).
      // Best-effort: failures surface as toast warnings only.
      const createdTrailerId = (variables?.trailer_id ?? "").toString().trim();
      if (result?.orderId && createdTrailerId !== "" && activeTenant) {
        const newOrderId = result.orderId;
        void syncTrailerBol({
          direction: "order-gains-trailer",
          orderId: newOrderId,
          tenantSlug: activeTenant,
        }).then((r) => {
          if (r.synced > 0) {
            queryClient.invalidateQueries({ queryKey: ["order-detail", newOrderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-count", newOrderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-images", newOrderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-images-count", newOrderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-extractions", newOrderId] });
          }
        });
      }
      if (result?.orderId) {
        onSuccess();
        if (onCreated) onCreated(result.orderId);
      } else if (result?.kind === "recurring" && (result as any).templateId) {
        const templateId = (result as any).templateId as string;
        onSuccess();
        // Best-effort: kick off inline generation so the template detail
        // page shows materialized instances immediately. Failures fall
        // back to the nightly cron.
        void supabase.functions
          .invoke("trigger-template-generation", { body: { template_id: templateId } })
          .catch((e) => console.warn("[useOrderMutations] inline template generation failed", e));
        if (onTemplateCreated) onTemplateCreated(templateId);
      } else {
        onSuccess();
      }
    },
    onError: (error: Error) => {
      const docs = parseConfirmDocsError(error.message);
      if (docs) {
        toast.error(formatMissingDocs(docs) || "Missing billing documentation to confirm this order.");
        return;
      }
      toast.error(getTractorDoubleBookingMessage(error) ?? error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data, originalOrder }: { id: string; data: OrderFormData; originalOrder: Order }) => {
      await ensureTenantContext();
      // Single-leg orders: the external trip is leg 1's active `trips` row.
      // Replace the deprecated parent column with it so every EAPI branch
      // below (PATCH, pivot, cancel, no-EAPI) targets the live trip rather
      // than a stale id (AOP-WENP-03315). Multi-leg keeps per-leg handling.
      {
        const [{ count: legCount }, legTrips] = await Promise.all([
          supabase.from("legs").select("id", { count: "exact", head: true }).eq("order_id", id),
          listActiveLegTripsForOrder(id),
        ]);
        const legOneTrip = resolveSingleLegTripId(legCount ?? 0, legTrips);
        if (legOneTrip !== undefined) {
          originalOrder = { ...originalOrder, eapi_trip_id: legOneTrip } as Order;
        }
      }
      // FK is authoritative for address fields — see `reconcileOrderFormFromFK`.
      data = reconcileOrderFormFromFK(data, locations);

      // ── Stale-status guard ──────────────────────────────────────────────
      // The edit dialog submits its whole form, including `status`, using the
      // snapshot it loaded when it opened. Anything that advanced the order
      // since then (driver "Ready to Depart", yard check-in, EAPI poller)
      // would be clobbered last-writer-wins. Re-read the live status here and
      // let `resolveEditStatus` decide; an explicit user status change still
      // wins. See AOP-VLTK-00046.
      const { data: liveStatusRow } = await supabase
        .from("orders")
        .select("status")
        .eq("id", id)
        .maybeSingle();
      const liveOrderStatus = (liveStatusRow?.status ?? null) as OrderStatus | null;
      const statusForWrite = resolveEditStatus({
        formStatus: data.status,
        snapshotStatus: originalOrder.status,
        liveStatus: liveOrderStatus,
      });
      if (
        liveOrderStatus &&
        statusForWrite === liveOrderStatus &&
        safeOrderStatus(data.status, originalOrder.status) !== liveOrderStatus
      ) {
        console.warn(
          "[useOrderMutations] dropped stale form status; keeping live DB status",
          {
            orderId: id,
            uniqueId: originalOrder.unique_id,
            formStatus: data.status,
            snapshotStatus: originalOrder.status,
            liveStatus: liveOrderStatus,
          },
        );
      }


      // ── Historical / billing-only edit gate ──
      // When OrderEditDialog flags a save as `_historicalEdit: true`
      // (admin editing a delivered/canceled order for billing/audit),
      // every external (EAPI) side effect in this mutation is suppressed:
      // no cancel, no PATCH, no pivot create, no per-leg sync, no
      // confirmation-integrity gates, no manual status push to EAPI.
      // The local DB write + change_events + order_change_log still run
      // so the audit trail captures who corrected what. See
      // `.lovable/memory/features/orders/edit-create-mutation-architecture.md`
      // for the full contract.
      const historicalEdit = (data as any)._historicalEdit === true;
      if (historicalEdit) {
        console.info(
          "[useOrderMutations] historical/billing edit — skipping all EAPI sync",
          { orderId: id, uniqueId: originalOrder.unique_id, status: originalOrder.status },
        );
      }

      // Multi-leg saves replace trips only via `syncPerLegEapiTrips` (Step 2);
      // the legacy order-level pivot left an untracked whole-load trip.
      const isMultiLegSave =
        data.order_type === "multi-leg" && Array.isArray(data.legs) && data.legs.length >= 2;
      // Edit cannot add legs; reject before any EAPI side effect.
      if (isMultiLegSave && data.legs.some((l) => !l.id)) {
        throw new Error("Cannot add new legs during edit — all legs must have an existing ID");
      }
      // True only once the legacy `orders.eapi_trip_id` trip is confirmed gone upstream.
      let legacyOrderTripRetired = false;

      // Determine if the new state is "no-EAPI" (bobtail trailer or Manual trip type)
      let newTrailerKind: string | null = null;
      let newTrailerTypeInfo: { kind: string; length_ft: number } | null = null;
      // Per-mutation cache for trailer_types lookups. The same id is resolved
      // 4-5x across single-leg pivot, multi-leg promote, per-leg pivot, per-leg
      // repair, and per-leg PATCH; cache them once so a single edit incurs at
      // most one DB hit per distinct trailer_type_id.
      const trailerTypeInfoCache = new Map<
        string,
        { kind: string; length_ft: number } | null
      >();
      const resolveTrailerTypeInfo = async (
        id: string | null | undefined,
      ): Promise<{ kind: string; length_ft: number } | null> => {
        if (!id) return null;
        if (trailerTypeInfoCache.has(id)) {
          return trailerTypeInfoCache.get(id) ?? null;
        }
        const { data: tt } = await supabase
          .from("trailer_types")
          .select("kind, length_ft")
          .eq("id", id)
          .single();
        const info = tt ? { kind: tt.kind, length_ft: tt.length_ft } : null;
        trailerTypeInfoCache.set(id, info);
        return info;
      };
      const effectiveTrailerTypeId = data.trailer_type_id || originalOrder.trailer_type_id;
      if (effectiveTrailerTypeId) {
        newTrailerTypeInfo = await resolveTrailerTypeInfo(effectiveTrailerTypeId);
        newTrailerKind = newTrailerTypeInfo?.kind ?? null;
      }
      const newStateSkipsEapi = isNoEapiState(
        newTrailerKind,
        data.trip_type,
        activeTenant,
      );

      // ── Pre-flight: confirmation-integrity guard ──
      // If the order is already confirmed (has an external trip), block any
      // attempt to clear truck or trailer — those are required to keep the
      // external trip valid.
      // Skipped in historical mode: the external trip is intentionally
      // not being touched, so the gate doesn't apply.
      if (
        !historicalEdit &&
        originalOrder.eapi_trip_id &&
        data.status !== "canceled" &&
        (
          (data.assigned_tractor_id ?? "") === "" ||
          // Bobtail orders intentionally have no trailer — don't gate on it.
          ((data.trailer_id ?? "").trim() === "" && newTrailerKind !== "bobtail")
        )
      ) {
        throw new Error("CONFIRMED_REQUIRES_TRUCK_TRAILER");
      }

      // ── Pre-flight: booked → confirmed promotion gate ──
      // Truck + trailer are required to confirm. Promotion is an explicit
      // manual action by dispatch — we only treat the update as a confirm
      // when the caller sets `status === "confirmed"`. There is no implicit
      // auto-confirm based on trailer assignment.
      //
      // The presence rules themselves (bobtail waiver, per-leg vs. parent
      // mirror, fail-closed on missing legs) live in `confirm-readiness.ts`,
      // shared with the Order Detail "Confirm Order" button and the dialogs'
      // `hasConfirmAssets`, so the button and this write path cannot disagree.
      const trailerIdSet = (data.trailer_id ?? "").trim() !== "";
      const tractorIdSet = (data.assigned_tractor_id ?? "") !== "";
      const wantsConfirm = data.status === "confirmed";
      // Bobtail = no trailer by definition. Skip trailer-presence and
      // trailer-on-site gates entirely when the effective trailer type kind
      // is bobtail.
      const isBobtail = newTrailerKind === "bobtail";
      // For multi-leg orders with per-leg assets, the parent's tractor is
      // intentionally empty — tractors live on each leg. The per-leg gate
      // below validates those. Only require parent tractor in the order-level
      // (single-leg or shared-asset multi-leg) case.
      const isPerLegAssets =
        data.order_type === "multi-leg" &&
        data.use_same_assets_for_all_legs === false;
      if (wantsConfirm && !historicalEdit) {
        // Resolve every trailer-type kind this check needs, then run the pure
        // rule. `bobtail` is the only kind that waives a trailer; an
        // unresolvable type keeps the trailer required (fail closed).
        const bobtailIds = new Set<string>();
        const noteBobtail = async (typeId: string | null | undefined) => {
          if (!typeId || bobtailIds.has(typeId)) return;
          const kind = (await resolveTrailerTypeInfo(typeId))?.kind ?? null;
          if (kind === "bobtail") bobtailIds.add(typeId);
        };
        const parentTypeId = effectiveTrailerTypeId || null;
        if (isBobtail && parentTypeId) bobtailIds.add(parentTypeId);
        const legs = isPerLegAssets
          ? (data.legs ?? []).map((leg, i) => ({
              tractorId: leg.assigned_tractor_id,
              trailerId: leg.trailer_id,
              // `data.trailer_type_id` mirrors leg 1 — later legs must read as
              // missing a trailer type rather than inherit leg 1's.
              trailerTypeId:
                leg.trailer_type_id ||
                (legMirrorsParentEquipment(i + 1, (data.legs ?? []).length)
                  ? data.trailer_type_id
                  : null) ||
                null,
              // Trip type genuinely falls back to the order's on every leg.
              tripType: leg.trip_type || data.trip_type || null,
              operatorId: leg.assigned_operator_id || null,
            }))
          : undefined;

        for (const leg of legs ?? []) await noteBobtail(leg.trailerTypeId);

        const missingAssets = missingConfirmRequirements({
          // Trip type is validated by the dialogs, not by this pre-flight.
          checkTripType: false,
          // Per-leg tractors are enforced just below, with their own sentinel.
          checkTractor: !isPerLegAssets,
          tractorId: data.assigned_tractor_id,
          trailerId: data.trailer_id,
          trailerTypeId: parentTypeId,
          tripType: data.trip_type,
          operatorId: data.assigned_operator_id,
          isBobtailTrailerType: (id) => !!id && bobtailIds.has(id),
          legs,
        });
        if (missingAssets.includes("operator")) {
          throw new Error(
            "Manual trips need an operator assigned before they can be confirmed.",
          );
        }
        if (missingAssets.includes("trailer type")) {
          throw new Error(
            "SVO, NVO and CI trips need a trailer type before they can be confirmed.",
          );
        }
        if (missingAssets.length > 0) throw new Error("CONFIRM_REQUIRES_TRUCK_TRAILER");

      }

      // ── Pre-flight: billing documentation gate (Document Confirm Gating) ──
      // A confirmed order must already carry whatever the
      // billing party requires (Tender ID / BOL / Tender) so it can be billed
      // immediately. Also cross-checks an API order's planned load status
      // against its trailer. Central chokepoint for every client confirm path.
      if (
        !isAdmin &&
        wantsConfirm &&
        !historicalEdit &&
        originalOrder.status !== "confirmed"
      ) {
        const billingPartyId =
          (data.billing_party_id ?? originalOrder.billing_party_id ?? null) || null;
        const { missing } = await fetchMissingConfirmDocs({
          orderId: id,
          billingPartyId,
          tenderId: data.tender_id ?? originalOrder.tender_id ?? null,
          tenantId,
          pendingBol: pendingBolValue(data),
        });
        if (missing.length > 0) throw new Error(confirmDocsErrorMessage(missing));
      }


      // ── Pre-flight: per-leg gate for multi-leg promotions ──
      // When the operator manages assets per-leg, every leg must have its own
      // tractor before we can confirm and create per-leg EAPI trips. The
      // shared-asset case is already covered by the order-level gate above.
      const isMultiLegPromotion =
        wantsConfirm &&
        data.order_type === "multi-leg" &&
        Array.isArray(data.legs) &&
        data.legs.length >= 2 &&
        !originalOrder.eapi_trip_id;
      if (
        isMultiLegPromotion &&
        data.use_same_assets_for_all_legs === false &&
        !historicalEdit
      ) {
        const missingLegTractor = data.legs.some(
          (leg) => !(leg.assigned_tractor_id ?? "").trim(),
        );
        if (missingLegTractor) {
          throw new Error("CONFIRM_REQUIRES_TRUCK_TRAILER");
        }
      }

      // ── Pre-flight: trailer must be checked in on site ──
      // An order can only be promoted to `confirmed` when its assigned
      // trailer has an active YMS check-in (current_location_status =
      // 'at_site'). Scope: this gate now applies ONLY when the order-level
      // origin (`ship_from_location_id`) is a YMS-tracked location. Leg
      // origins, destinations, and free-text origins are intentionally
      // excluded — without an origin yard the trailer has no canonical
      // place to check in, so the gate is N/A.
      //
      // Skipped when: order is already `confirmed` (no fresh promotion),
      // trailer kind is bobtail, no trailer code, or the origin has no
      // linked location FK.
      //
      // Fail-closed: if the origin location lookup errors or the row is
      // missing, enforce rather than silently skipping.
      if (
        !historicalEdit &&
        wantsConfirm &&
        originalOrder.status !== "confirmed" &&
        tenantId &&
        !isBobtail
      ) {
        const code = (data.trailer_id ?? "").trim();
        const originLocationId =
          ((data as any).ship_from_location_id as string | null | undefined) ??
          ((originalOrder as any).ship_from_location_id as string | null | undefined) ??
          null;
        if (code && originLocationId) {
          const sb = supabase as any;
          const { data: originLoc, error: originLocErr } = await sb
            .from("locations")
            .select("track_in_yms")
            .eq("id", originLocationId)
            .maybeSingle();
          // Fail-closed: error or missing row -> enforce the gate.
          const enforceGate =
            !!originLocErr || !originLoc || !!(originLoc as any).track_in_yms;
          if (enforceGate) {
            const { data: trailerRow, error: trailerErr } = await supabase
              .from("trailers")
              .select("current_location_status, current_location_id")
              .eq("tenant_id", tenantId)
              .eq("trailer_id", code)
              .maybeSingle();
            if (trailerErr) throw trailerErr;
            const onSite =
              !!trailerRow &&
              trailerRow.current_location_status === "at_site" &&
              !!trailerRow.current_location_id;
            if (!onSite) {
              throw new Error(`CONFIRM_REQUIRES_TRAILER_ON_SITE::${code}`);
            }
          }
        }
      }

      // ── Step 1: EAPI sync FIRST (external-first) ──
      // Historical/billing-only edits bypass this entire block — see the
      // `historicalEdit` gate at the top of `mutationFn`.
      if (historicalEdit) {
        // no-op: skip cancel/no-EAPI-switch/pivot/PATCH/per-leg sync
      } else if (data.status === "canceled") {
        // Cancellation → cancel order-level trip (if any) AND fan-out cancel
        // every active per-leg trip. Phase 4 multi-leg orders may have no
        // order-level `eapi_trip_id` at all — only per-leg trips — so we must
        // run the fan-out unconditionally on cancel.
        const readyClient = await waitForEapiClient();
        if (readyClient) {
          if (originalOrder.eapi_trip_id) {
            try {
              await readyClient.cancelTrip(originalOrder.eapi_trip_id, data.cancellation_reason || undefined);
              console.log("EAPI trip cancelled:", originalOrder.eapi_trip_id);
            } catch (cancelErr: any) {
              if (isAlreadyCancelledUpstreamError(cancelErr)) {
                console.log(
                  `[cancel] upstream trip ${originalOrder.eapi_trip_id} already cancelled, proceeding with local cancel`,
                );
              } else {
                console.warn("Order-level EAPI cancel failed:", cancelErr?.message || cancelErr);
                toast.warning(
                  `Failed to cancel external trip: ${cancelErr?.message || "Unknown error"}. Local cancel will proceed.`,
                );
              }
            }
          }
          // Per-leg cancel fan-out: covers (a) sibling per-leg trips on a
          // dual-write order and (b) Phase 4 orders with only per-leg trips.
          await cancelOtherActiveLegTrips(
            id,
            originalOrder.eapi_trip_id ?? null,
            readyClient,
            data.cancellation_reason || "Order cancelled",
            "order_cancelled",
          );
        }
      } else if (newStateSkipsEapi) {
        // ── Switching to bobtail/Manual on an order that has any active external trip ──
        // This branch must fire when EITHER (a) the parent order has its own
        // `eapi_trip_id` (legacy single-leg path) OR (b) there are active
        // per-leg trips on `public.trips` (multi-leg path — parent has no
        // `eapi_trip_id` at all). Previously the gate only checked the
        // parent, which silently skipped the entire upstream cancel for
        // multi-leg orders and left orphan EAPI trips behind (AOP-DETM-01596).
        const activeLegTripsForSkip = await listActiveLegTripsForOrder(id);
        const hasParentTrip = !!originalOrder.eapi_trip_id;
        const hasLegTrips = activeLegTripsForSkip.length > 0;
        if (hasParentTrip || hasLegTrips) {
          const switchedToManual =
            data.trip_type === "Manual" && originalOrder.trip_type !== "Manual";
          const mode: "cancel" | "abandon" = switchedToManual ? "abandon" : "cancel";
          const pivotReasonSuffix = ((data as any)._pivotReason as string | undefined)?.trim() || "";
          const reasonLabel = switchedToManual
            ? pivotReasonSuffix || "Pivoted to manual"
            : "Switched to Bobtail / Manual trip type";
          const readyClient = await waitForEapiClient();
          if (readyClient) {
            if (hasParentTrip) {
              try {
                if (mode === "abandon") {
                  await readyClient.abandonTrip(originalOrder.eapi_trip_id!, reasonLabel);
                  console.log("EAPI trip abandoned (switched to Manual):", originalOrder.eapi_trip_id);
                } else {
                  await readyClient.cancelTrip(originalOrder.eapi_trip_id!, reasonLabel);
                  console.log("EAPI trip cancelled (switched to no-EAPI state):", originalOrder.eapi_trip_id);
                }
                legacyOrderTripRetired = isMultiLegSave;
              } catch (cancelErr: any) {
                if (isAlreadyCancelledUpstreamError(cancelErr)) {
                  legacyOrderTripRetired = isMultiLegSave;
                  console.log(
                    `[${mode}] upstream trip ${originalOrder.eapi_trip_id} already terminal on no-EAPI switch, continuing`,
                  );
                } else {
                  console.warn(`Failed to ${mode} parent external trip on no-EAPI switch:`, cancelErr);
                  toast.warning(
                    `Failed to ${mode} external trip: ${cancelErr.message || "Unknown error"}. Trip ID will be cleared locally.`,
                  );
                }
              }
            }
            // Always fan out to per-leg trips — handles both single-leg (no
            // op if empty) and multi-leg (cancels every active leg trip).
            try {
              await cancelOtherActiveLegTrips(
                id,
                originalOrder.eapi_trip_id ?? null,
                readyClient,
                reasonLabel,
                "no_eapi_switch",
                mode,
              );
            } catch (fanoutErr: any) {
              console.warn(`[${mode}] per-leg fan-out failed on no-EAPI switch:`, fanoutErr);
              toast.warning(
                `Some external trips could not be ${mode === "abandon" ? "abandoned" : "cancelled"} (${fanoutErr?.message || fanoutErr}). They may require manual cleanup.`,
              );
            }
          }
        }
        // Single-leg only; multi-leg clears via `legacyOrderTripRetired`.
        if (!isMultiLegSave) {
          (data as any)._clearEapiTripId = true;
        }
      } else if (
        !isMultiLegSave &&
        originalOrder.eapi_trip_id &&
        originalOrder.status !== "in_transit" &&
        originalOrder.status !== "delivered"
      ) {
        const tractorChanged = (data.assigned_tractor_id || "") !== (originalOrder.assigned_tractor_id || "");
        const trailerChanged = (data.trailer_id || "").trim() !== (originalOrder.trailer_id || "").trim();

        if (tractorChanged || trailerChanged) {
          const pivotReasonSuffix = ((data as any)._pivotReason as string | undefined)?.trim() || "";
          const basePivotReason =
            tractorChanged && trailerChanged
              ? "Truck/Trailer pivot"
              : trailerChanged
                ? "Trailer pivot"
                : "Truck pivot";
          const pivotReason = pivotReasonSuffix
            ? `${basePivotReason}: ${pivotReasonSuffix}`
            : basePivotReason;
          // ── Trip Pivot: cancel old trip, create new one ──
          const readyClient = await waitForEapiClient();
          if (!readyClient) {
            throw new Error("EAPI authentication not available — cannot perform trip pivot");
          }

          // 1. Cancel old trip
          console.log("EAPI trip pivot: cancelling old trip", originalOrder.eapi_trip_id);
          try {
            await readyClient.cancelTrip(originalOrder.eapi_trip_id, pivotReason);
            console.log("EAPI trip pivot: old trip cancelled");
          } catch (cancelErr: any) {
            throw new Error(`Failed to cancel external trip: ${cancelErr.message || "Unknown error"}`);
          }

          // 1b. Fan-out cancel any sibling per-leg trips. Tractor/trailer
          // pivots are order-wide changes, so leaving sibling leg trips
          // pointing at the old assets would orphan them upstream (this is
          // exactly what bit AOS3-AURY-00166).
          const pivotSupersedeReason =
            tractorChanged && trailerChanged
              ? "truck_trailer_pivot"
              : trailerChanged
                ? "trailer_pivot"
                : "tractor_pivot";
          await cancelOtherActiveLegTrips(
            id,
            originalOrder.eapi_trip_id,
            readyClient,
            pivotReason,
            pivotSupersedeReason,
          );

          // 2. Create new trip with the same order name
          const newTractor = tractors?.find(t => t.id === data.assigned_tractor_id) ?? null;
          const trailerTypeInfo = await resolveTrailerTypeInfo(
            data.trailer_type_id || originalOrder.trailer_type_id,
          );

          // Trip pivot recreate body: data.* may carry form-layer local-naive
          // times that would otherwise be treated as UTC. Resolve them at the
          // (possibly new) ship-from/ship-to location before merging into the
          // originalOrder spread.
          const pivotFromLoc = findLocationByAddress(
            locations,
            data.ship_from_street_address || originalOrder.ship_from_street_address,
            data.ship_from_city || originalOrder.ship_from_city,
          );
          const pivotToLoc = findLocationByAddress(
            locations,
            data.ship_to_street_address || originalOrder.ship_to_street_address,
            data.ship_to_city || originalOrder.ship_to_city,
          );
          const pivotReadyUtc = data.ready_time
            ? formLocalToUtc(data.ready_time) ?? originalOrder.ready_time
            : originalOrder.ready_time;
          const pivotDeliveryUtc = data.requested_delivery_time
            ? formLocalToUtc(data.requested_delivery_time) ?? originalOrder.requested_delivery_time
            : originalOrder.requested_delivery_time;
          const pivotReadyEndUtc = data.ready_time_end
            ? formLocalToUtc(data.ready_time_end) ?? pivotReadyUtc
            : (originalOrder.ready_time_end ?? pivotReadyUtc);
          const pivotDeliveryEndUtc = data.requested_delivery_time_end
            ? formLocalToUtc(data.requested_delivery_time_end) ?? pivotDeliveryUtc
            : (originalOrder.requested_delivery_time_end ?? pivotDeliveryUtc);

          const tripBody = mapOrderToCreateTrip(
            {
              ...originalOrder,
              ...data,
              assigned_tractor_id: data.assigned_tractor_id,
              ready_time: pivotReadyUtc,
              requested_delivery_time: pivotDeliveryUtc,
              ready_time_end: pivotReadyEndUtc,
              requested_delivery_time_end: pivotDeliveryEndUtc,
              // Preserve FK location IDs (see promotion path note).
              ship_from_location_id:
                ((data as { ship_from_location_id?: string | null }).ship_from_location_id || null)
                ?? originalOrder.ship_from_location_id
                ?? null,
              ship_to_location_id:
                ((data as { ship_to_location_id?: string | null }).ship_to_location_id || null)
                ?? originalOrder.ship_to_location_id
                ?? null,
            } as unknown as Order,
            newTractor,
            trailerTypeInfo,
            locations,
            await buildCustomFieldsForOrder(readyClient, eapiEnvironmentRef.current, {
              customerShortCode: originalOrder.billing_party?.short_code,
              tripType: (data as any).trip_type ?? (originalOrder as any).trip_type,
              trailerId: data.trailer_id ?? originalOrder.trailer_id,
              operator1Email: drivers?.find(
                (d) => d.id === (data.assigned_operator_id ?? originalOrder.assigned_operator_id),
              )?.email,
              operator2Email: drivers?.find(
                (d) => d.id === ((data as any).assigned_operator_2_id ?? (originalOrder as any).assigned_operator_2_id),
              )?.email,
            }),
          );
          console.log("EAPI trip pivot: creating new trip", tripBody);

          try {
            assertTripBodyHasLatLng(tripBody, {
              flow: "tractor pivot",
              uniqueId: originalOrder.unique_id,
            });
            const res = await readyClient.createTrip(tripBody);
            if (res?.status && res.status >= 400) {
              throw new Error(`EAPI returned ${res.status}`);
            }
            const newTripId = res?.data?.id;
            if (!newTripId) {
              throw new Error("EAPI response did not include a trip ID");
            }
            // Store the new trip ID so the local DB write uses it
            (data as any)._newEapiTripId = newTripId;
            console.log("EAPI trip pivot: new trip created", newTripId);
          } catch (createErr: any) {
            // Cancellation succeeded but re-creation failed — clear the stale trip ID
            console.warn("EAPI trip pivot: re-creation failed", createErr);
            // PRECONDITION: we only reach this branch after a successful
            // upstream cancel of `originalOrder.eapi_trip_id` AND a fan-out
            // cancel of sibling leg trips above. Do NOT introduce code paths
            // that null `eapi_trip_id` without a successful upstream cancel
            // first — that is what orphaned AOS3-AURY-00166's leg 1 trip.
            (data as any)._clearEapiTripId = true;
            toast.warning("External trip was cancelled but re-creation failed. The order will be saved without an external trip.");
          }
        } else {
          // Normal update → build delta and PUT only changed fields
          const assignedTractor = tractors?.find(t => t.id === (data.assigned_tractor_id || originalOrder.assigned_tractor_id)) ?? null;

          const trailerTypeInfo = await resolveTrailerTypeInfo(
            data.trailer_type_id || originalOrder.trailer_type_id,
          );

          const delta = buildEapiDelta(originalOrder, data, assignedTractor, trailerTypeInfo, locations);
          if (delta) {
            const readyClient = await waitForEapiClient();
            if (readyClient) {
              console.log("EAPI patchTrip delta payload:", delta);
              assertTripBodyHasLatLng(delta, {
                flow: "patch delta",
                uniqueId: originalOrder.unique_id,
                eapiTripId: originalOrder.eapi_trip_id,
              });
              await readyClient.patchTrip(originalOrder.eapi_trip_id, delta);
              console.log("EAPI trip patched:", originalOrder.eapi_trip_id);
            }
          }

        }
      }

      // ── Step 2: Local DB write (only runs if EAPI succeeded or was skipped) ──
      if (isMultiLegSave) {
        const legs = data.legs;
        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];
        const useSameAssetsUpd = data.use_same_assets_for_all_legs !== false;
        const useSameTripTypeUpd = data.use_same_trip_type_for_all_legs !== false;

        // ── Snapshot pre-update leg rows for EAPI PATCH + change-log diffing ──
        // We need the persisted values BEFORE the leg UPDATE loop runs so we
        // can (a) build per-leg EAPI deltas and (b) emit accurate
        // order_change_log entries.
        const legSnapshots = new Map<string, any>();
        {
          const legIds = legs.map((l) => l.id).filter(Boolean) as string[];
          if (legIds.length > 0) {
            const snapCols =
              "id, leg_sequence, ship_from_street_address, ship_from_city, ship_from_province_state, ship_from_postal_zip_code, ship_from_country, ship_to_street_address, ship_to_city, ship_to_province_state, ship_to_postal_zip_code, ship_to_country, ready_time, ready_time_end, requested_delivery_time, requested_delivery_time_end, trailer_type_id, trailer_id, assigned_tractor_id, assigned_operator_id, assigned_operator_2_id, trip_type, ship_from_location_id, ship_to_location_id";
            const { data: legsRows } = await supabase
              .from("legs" as any)
              .select(`${snapCols}, driver_status`)
              .in("id", legIds);
            (legsRows || []).forEach((r: any) => legSnapshots.set(r.id, r));
          }
        }

        // ── Orphaned order-level trip cleanup (AOP-HTSH-06075) ──
        // An order confirmed while single-leg keeps its trip id on
        // `orders.eapi_trip_id`. Once per-leg trips exist that trip belongs to
        // no leg — abandon it upstream and only then clear our pointer.
        // Never withdraws a trip still attached to a leg.
        const retireOrphanOrderTrip = async (
          eapiClientForRetire: any,
          label: string,
        ) => {
          if (historicalEdit) return;
          if (!originalOrder.eapi_trip_id) return;
          if (legacyOrderTripRetired) return;
          const res = await retireOrphanedOrderLevelTrip({
            orderId: id,
            orderLevelTripId: originalOrder.eapi_trip_id,
            client: eapiClientForRetire,
            reasonLabel: `Superseded by per-leg external trips (${label})`,
          });
          if (res.retired) {
            legacyOrderTripRetired = true;
          } else if (res.reason === "upstream_failed") {
            toast.warning(
              `The order's previous external trip ${originalOrder.eapi_trip_id} could not be retired (${res.error}). It may still be live upstream.`,
            );
          }
        };

        // ── Multi-leg booked → confirmed promotion: per-leg EAPI createTrip ──
        // Mirrors the single-leg promotion block below, but fans out to one
        // EAPI trip per leg. Sync-then-persist: we POST every trip BEFORE
        // touching local DB. If any leg fails, cancel the trips we already
        // created and abort so the order stays in `booked`.
        const promoteMultiLeg =
          !historicalEdit &&
          data.status === "confirmed" &&
          originalOrder.status !== "confirmed" &&
          !originalOrder.eapi_trip_id &&
          !newStateSkipsEapi;
        const createdLegTripIds: Array<{ legSequence: number; eapiTripId: string }> = [];
        if (promoteMultiLeg) {
          const client = await waitForEapiClient();
          if (!client) {
            throw new Error(
              `EAPI authentication did not become ready within ${EAPI_READY_WAIT_MS / 1000} seconds`,
            );
          }

          // Resolve trailer type info once (shared across legs when toggle on).
          let sharedTrailerTypeInfo: { kind: string; length_ft: number } | null = newTrailerTypeInfo;
          // Custom fields are per-leg: the "Trailer ID" custom field must
          // reflect THIS leg's trailer, not just leg 1's. Cache by trailerId
          // so we still only compose entries once per distinct value.
          const orderLevelTrailerFallback = data.trailer_id ?? originalOrder.trailer_id;
          const legFieldCache = new Map<string, Awaited<ReturnType<typeof buildCustomFieldsForOrder>>>();
          const getLegCustomFields = async (
            trailerId: string | null | undefined,
            operator1Id: string | null | undefined,
            operator2Id: string | null | undefined,
            tripType?: string | null,
          ) => {
            const op1Email = drivers?.find((d) => d.id === (operator1Id || ""))?.email ?? null;
            const op2Email = drivers?.find((d) => d.id === (operator2Id || ""))?.email ?? null;
            const key = `${trailerId ?? "__none__"}|${op1Email ?? ""}|${op2Email ?? ""}|${tripType ?? ""}`;
            if (legFieldCache.has(key)) return legFieldCache.get(key);
            const fields = await buildCustomFieldsForOrder(
              client,
              eapiEnvironmentRef.current,
              {
                customerShortCode: originalOrder.billing_party?.short_code,
                trailerId,
                operator1Email: op1Email,
                operator2Email: op2Email,
                tripType,
              },
            );
            legFieldCache.set(key, fields);
            return fields;
          };
          // Payloads are built sequentially so the trailer-type and
          // custom-field caches are still hit once per distinct value; only
          // the network calls fan out below.
          const tripRequests: PromoteTripRequest[] = [];
          for (let i = 0; i < legs.length; i++) {
            const leg = legs[i];
            const legSeq = i + 1;
            const legTractorId = useSameAssetsUpd
              ? data.assigned_tractor_id
              : leg.assigned_tractor_id;
            const legTrailerTypeId = useSameAssetsUpd
              ? data.trailer_type_id
              : (leg.trailer_type_id ||
                 (legMirrorsParentEquipment(legSeq, legs.length)
                   ? data.trailer_type_id
                   : null));


            const legTractor =
              tractors?.find((t) => t.id === (legTractorId || "")) ?? null;

            // The cache handles dedupe across legs; the shared-asset shortcut
            // is kept as an in-memory fast-path so the loop avoids any await
            // when the value is already known.
            let legTrailerTypeInfo: { kind: string; length_ft: number } | null = null;
            if (useSameAssetsUpd) {
              legTrailerTypeInfo = sharedTrailerTypeInfo;
            } else if (legTrailerTypeId && legTrailerTypeId === data.trailer_type_id && sharedTrailerTypeInfo) {
              legTrailerTypeInfo = sharedTrailerTypeInfo;
            } else {
              legTrailerTypeInfo = await resolveTrailerTypeInfo(legTrailerTypeId);
            }

            // Per-leg EAPI eligibility: each leg's trip type (falling back
            // to the parent) and its own trailer type kind decide whether
            // an external trip should be created. Manual / bobtail legs
            // are local-only and must NOT generate an EAPI trip even when
            // sibling legs do.
            const effectiveLegTripType = useSameTripTypeUpd
              ? data.trip_type
              : (leg.trip_type || data.trip_type);
            const legSkipsEapi = isNoEapiState(
              legTrailerTypeInfo?.kind ?? null,
              effectiveLegTripType,
              activeTenant,
            );
            if (legSkipsEapi) {
              console.log(
                `[multi-leg promote] skipping EAPI createTrip for leg ${legSeq} (trip_type=${effectiveLegTripType}, trailer_kind=${legTrailerTypeInfo?.kind ?? "null"})`,
              );
              continue;
            }

            const fromLoc = findLocationByAddress(
              locations,
              leg.ship_from_street_address,
              leg.ship_from_city,
            );
            const toLoc = findLocationByAddress(
              locations,
              leg.ship_to_street_address,
              leg.ship_to_city,
            );
            const readyUtc = formLocalToUtc(leg.ready_time);
            const deliveryUtc = formLocalToUtc(leg.requested_delivery_time);
            const readyEndUtc = formLocalToUtc(leg.ready_time_end || leg.ready_time);
            const deliveryEndUtc = formLocalToUtc(leg.requested_delivery_time_end || leg.requested_delivery_time);

            const tripBody = mapLegToCreateTrip(
              {
                leg_sequence: legSeq,
                ship_from_street_address: leg.ship_from_street_address,
                ship_from_city: leg.ship_from_city,
                ship_from_province_state: leg.ship_from_province_state || null,
                ship_from_postal_zip_code: leg.ship_from_postal_zip_code || null,
                ship_from_country: leg.ship_from_country || null,
                ship_to_street_address: leg.ship_to_street_address,
                ship_to_city: leg.ship_to_city,
                ship_to_province_state: leg.ship_to_province_state || null,
                ship_to_postal_zip_code: leg.ship_to_postal_zip_code || null,
                ship_to_country: leg.ship_to_country || null,
                ready_time: readyUtc ?? "",
                ready_time_end: readyEndUtc,
                requested_delivery_time: deliveryUtc ?? "",
                requested_delivery_time_end: deliveryEndUtc,
              },
              originalOrder.unique_id,
              legTractor,
              legTrailerTypeInfo,
              locations,
              await getLegCustomFields(
                useSameAssetsUpd
                  ? orderLevelTrailerFallback
                  : ((leg.trailer_id ?? orderLevelTrailerFallback) || null),
                useSameAssetsUpd
                  ? data.assigned_operator_id
                  : (leg.assigned_operator_id ?? data.assigned_operator_id),
                useSameAssetsUpd
                  ? (data as any).assigned_operator_2_id
                  : (leg.assigned_operator_2_id ?? (data as any).assigned_operator_2_id),
                effectiveLegTripType,
              ),
            );

            console.log(
              `EAPI createTrip payload (multi-leg booked → confirmed) leg ${legSeq}:`,
              tripBody,
            );
            tripRequests.push({
              legSequence: legSeq,
              tripBody,
              label: `multi-leg confirm leg ${legSeq}`,
              assert: {
                flow: "multi-leg confirm",
                parentUniqueId: originalOrder.unique_id,
                legSequence: legSeq,
              },
            });
          }

          const created = await createTripsForLegs({
            requests: tripRequests,
            create: (req) =>
              createEapiTripChecked(client, req.tripBody as EapiCreateTripBody, {
                label: req.label,
                assert: req.assert as any,
              }),
            cancel: (tripId, reason) => client.cancelTrip(tripId, reason),
            rollbackReason: "Multi-leg confirmation rollback",
            logLabel: "multi-leg promote",
          });
          createdLegTripIds.push(...created);

          // The order may have carried an order-level trip from when it was
          // single-leg. Now that per-leg trips exist, retire it upstream so we
          // don't leave a live orphan nobody can see from the order.
          await retireOrphanOrderTrip(client, "multi-leg promote");
        }

        // ── Per-leg EAPI PATCH for time/address/window/trailer-type edits ──
        // Skip when we're about to create per-leg trips (promotion above) —
        // those rows don't exist yet upstream. Sync-then-persist: a PATCH
        // failure aborts the local UPDATE so the DB never diverges from EAPI.
        // Per-leg pivot + repair are handled in the block immediately above so
        // legs whose trip was just cancelled+recreated (or freshly repaired)
        // skip the PATCH path.
        const pivotedLegIds = new Set<string>();
        const repairedLegIds = new Set<string>();
        const unlinkedTrips: UnlinkedReplacementTrip[] = [];
        if (!promoteMultiLeg && !historicalEdit) {
          // ── Per-leg Trip Pivot + repair via the unified helper ──
          // Multi-leg orders with per-leg EAPI trips are NOT covered by the
          // order-level pivot block (gated on `originalOrder.eapi_trip_id`),
          // so tractor changes here would otherwise silently bypass EAPI.
          // Repair also covers legs with no active trip (e.g.
          // AOP-DETM-01596-L1 whose trip was superseded with no replacement).
          const pivotRepairClient = await waitForEapiClient();

          if (pivotRepairClient) {
            const result = await syncPerLegEapiTrips({
              orderId: id,
              originalOrder,
              data,
              legs,
              legSnapshots,
              useSameAssetsUpd,
              useSameTripTypeUpd,
              tenantId,
              tenantSlug: activeTenant,
              tractors,
              locations,
              drivers,
              resolveTrailerTypeInfo,
              client: pivotRepairClient,
              eapiEnvironment: eapiEnvironmentRef.current,
              pivotReasonSuffix:
                ((data as any)._pivotReason as string | undefined)?.trim() || undefined,
              enableRepair: true,
            });
            result.pivotedLegIds.forEach((id) => pivotedLegIds.add(id));
            result.repairedLegIds.forEach((id) => repairedLegIds.add(id));
            unlinkedTrips.push(...result.unlinkedTrips);
            await retireOrphanOrderTrip(pivotRepairClient, "per-leg repair");
          } else {
            console.warn(
              "[per-leg sync] EAPI client unavailable; skipping per-leg pivot/repair for", id,
            );
          }
        }

        if (!promoteMultiLeg && !historicalEdit) {
          const activeLegTrips = await listActiveLegTripsForOrder(id);
          if (activeLegTrips.length > 0) {
            const tripByLegId = new Map(activeLegTrips.map((t) => [t.leg_id, t]));
            let patchClient: Awaited<ReturnType<typeof waitForEapiClient>> | null = null;
            for (let i = 0; i < legs.length; i++) {
              const leg = legs[i];
              const snap = leg.id ? legSnapshots.get(leg.id) : null;
              if (!snap) continue;
              const trip = leg.id ? tripByLegId.get(leg.id) : null;
              if (!trip) continue;
              // Skip legs that were just pivoted/repaired — their trip body
              // already reflects the latest form values.
              if (leg.id && (pivotedLegIds.has(leg.id) || repairedLegIds.has(leg.id))) continue;
              const legSeq = snap.leg_sequence ?? i + 1;

              const effectiveTripType = useSameTripTypeUpd
                ? data.trip_type
                : (leg.trip_type || data.trip_type);
              const legTrailerTypeId = useSameAssetsUpd
                ? (data.trailer_type_id || originalOrder.trailer_type_id)
                : (leg.trailer_type_id ||
                   (legMirrorsParentEquipment(legSeq, legs.length)
                     ? data.trailer_type_id || originalOrder.trailer_type_id
                     : null));

              const legTrailerTypeInfo = await resolveTrailerTypeInfo(legTrailerTypeId);
              if (
                isNoEapiState(
                  legTrailerTypeInfo?.kind ?? null,
                  effectiveTripType,
                  activeTenant,
                )
              ) {
                continue;
              }

              const legTractorId = useSameAssetsUpd
                ? data.assigned_tractor_id
                : leg.assigned_tractor_id;
              const legTractor = tractors?.find((t) => t.id === (legTractorId || "")) ?? null;

              const synthOriginal = {
                ...snap,
                eapi_trip_id: trip.eapi_trip_id,
              } as unknown as Order;
              const synthUpdated = {
                ship_from_street_address: leg.ship_from_street_address,
                ship_from_city: leg.ship_from_city,
                ship_from_province_state: leg.ship_from_province_state,
                ship_from_postal_zip_code: leg.ship_from_postal_zip_code,
                ship_from_country: leg.ship_from_country,
                ship_to_street_address: leg.ship_to_street_address,
                ship_to_city: leg.ship_to_city,
                ship_to_province_state: leg.ship_to_province_state,
                ship_to_postal_zip_code: leg.ship_to_postal_zip_code,
                ship_to_country: leg.ship_to_country,
                ready_time: leg.ready_time,
                ready_time_end: leg.ready_time_end,
                requested_delivery_time: leg.requested_delivery_time,
                requested_delivery_time_end: leg.requested_delivery_time_end,
                trailer_type_id: legTrailerTypeId,
                ship_from_location_id: (leg as any).ship_from_location_id ?? null,
                ship_to_location_id: (leg as any).ship_to_location_id ?? null,
              } as unknown as OrderFormData;

              const delta = buildEapiDelta(
                synthOriginal,
                synthUpdated,
                legTractor,
                legTrailerTypeInfo,
                locations,
              );
              if (!delta) continue;

              if (!patchClient) {
                patchClient = await waitForEapiClient();
              }
              if (!patchClient) {
                throw new Error(
                  "EAPI authentication not available — cannot PATCH per-leg trip",
                );
              }
              try {
                console.log(
                  `[multi-leg PATCH] leg ${legSeq} trip ${trip.eapi_trip_id} delta:`,
                  delta,
                );
                assertTripBodyHasLatLng(delta, {
                  flow: "multi-leg patch delta",
                  uniqueId: originalOrder.unique_id,
                  eapiTripId: trip.eapi_trip_id,
                  legSequence: legSeq,
                });
                await patchClient.patchTrip(trip.eapi_trip_id, delta);
                console.log(
                  `[multi-leg PATCH] leg ${legSeq} trip ${trip.eapi_trip_id} patched`,
                );
                // Bump the mirror row so stale-detection downstream has a
                // reliable last-touched timestamp for this trip.
                await touchActiveLegTrip(trip.trip_row_id);
              } catch (err: any) {
                throw new Error(
                  `Failed to PATCH external trip for leg ${legSeq}: ${err?.message || err}`,
                );
              }
            }
          }
        }

        // Update parent order shared fields + recalculated span.
        // `status` is coerced: a controlled Select whose current value has
        // no matching option (e.g. `in_transit` on a non-Manual order) can
        // emit "" — writing that fails with
        // `invalid input value for enum order_status: ""`.
        const parentUpdate: Record<string, unknown> = {
          status: statusForWrite,
          billing_party_id: data.billing_party_id,
          tender_id: data.tender_id || null,
          trip_type: data.trip_type || null,
          pending_bol: pendingBolValue(data),
          // Parent asset fields mirror leg 1 when assets are per-leg — writing
          // the order-level form values here would leave a stale assignment
          // on the parent after a per-leg truck removal/pivot, which other
          // orders' dropdowns then read as "booked".
          ...resolveChangedParentAssetMirror(
            data,
            originalOrder,
            firstLeg.id ? legSnapshots.get(firstLeg.id) : null,
          ),
          ship_from_street_address: firstLeg.ship_from_street_address,
          ship_from_city: firstLeg.ship_from_city,
          ship_from_province_state: firstLeg.ship_from_province_state || null,
          ship_from_postal_zip_code: firstLeg.ship_from_postal_zip_code || null,
          ship_from_country: firstLeg.ship_from_country || null,
          ship_to_street_address: lastLeg.ship_to_street_address,
          ship_to_city: lastLeg.ship_to_city,
          ship_to_province_state: lastLeg.ship_to_province_state || null,
          ship_to_postal_zip_code: lastLeg.ship_to_postal_zip_code || null,
          ship_to_country: lastLeg.ship_to_country || null,
          ship_from_location_id:
            ((firstLeg as any).ship_from_location_id as string | null | undefined)
            ?? findLocationByAddress(locations, firstLeg.ship_from_street_address, firstLeg.ship_from_city)?.id
            ?? null,
          ship_to_location_id:
            ((lastLeg as any).ship_to_location_id as string | null | undefined)
            ?? findLocationByAddress(locations, lastLeg.ship_to_street_address, lastLeg.ship_to_city)?.id
            ?? null,
          ready_time: formLocalToUtc(firstLeg.ready_time),
          requested_delivery_time: formLocalToUtc(lastLeg.requested_delivery_time),
          ready_time_end: formLocalToUtc(firstLeg.ready_time_end || firstLeg.ready_time),
          requested_delivery_time_end: formLocalToUtc(lastLeg.requested_delivery_time_end || lastLeg.requested_delivery_time),
          ...orderAdvancedPayload({
            origin_shuttle_carrier_name: firstLeg.origin_shuttle_carrier_name,
            origin_shuttle_carrier_dot: firstLeg.origin_shuttle_carrier_dot,
            destination_shuttle_carrier_name: lastLeg.destination_shuttle_carrier_name,
            destination_shuttle_carrier_dot: lastLeg.destination_shuttle_carrier_dot,
            freight_weight_lbs: data.freight_weight_lbs,
            commodity_code: data.commodity_code,
            commodity_description: data.commodity_description,
          }),
        };

        // Add cancellation fields for multi-leg
        if (data.status === "canceled") {
          parentUpdate.canceled_at = new Date().toISOString();
          parentUpdate.cancellation_reason = data.cancellation_reason || null;
          parentUpdate.cancellation_notes = data.cancellation_notes || null;
        }

        // Capture promotion attribution on the parent (set once).
        if (
          promoteMultiLeg &&
          !(originalOrder as any).confirmed_by_user_id
        ) {
          const { data: authData } = await supabase.auth.getUser();
          if (authData?.user?.id) {
            parentUpdate.confirmed_by_user_id = authData.user.id;
            parentUpdate.confirmed_at = new Date().toISOString();
          }
        }

        // Write only columns whose value actually changed. Full-payload
        // writes re-send untouched values (e.g. the leg-1 asset mirror on a
        // stale parent row), which trips server-side locks like the
        // post-in_transit tractor freeze even when nobody edited that field.
        // Only sanctioned write to the deprecated column (see AGENTS.md).
        if (legacyOrderTripRetired) {
          parentUpdate.eapi_trip_id = null;
        }
        const parentWrite = diffUpdate(originalOrder as any, parentUpdate);
        if (Object.keys(parentWrite).length > 0) {
          const { error: parentError } = await supabase.from("orders").update(parentWrite).eq("id", id);
          if (parentError) throw parentError;
        }

        // Update each leg. Leg rows live in `public.legs`.
        for (const leg of legs) {
          const legUpdate = {
            // The edit-form confirmation path creates every eligible external
            // trip and promotes the parent. It must also persist the matching
            // local status on every leg; external trips initially report
            // `booked`, which is not a substitute for this write.
            driver_status: legDriverStatusForOrderEdit(
              legSnapshots.get(leg.id)?.driver_status,
              promoteMultiLeg,
            ),
            // Per-leg asset assignment (Phase 4 plumbing). Mirror order-level
            // values when toggle is on; otherwise use the leg's own values.
            assigned_tractor_id:
              (useSameAssetsUpd ? data.assigned_tractor_id : leg.assigned_tractor_id) || null,
            assigned_operator_id:
              (useSameAssetsUpd ? data.assigned_operator_id : leg.assigned_operator_id) || null,
            assigned_operator_2_id:
              (useSameAssetsUpd ? data.assigned_operator_2_id : leg.assigned_operator_2_id) || null,
            trailer_type_id:
              (useSameAssetsUpd ? data.trailer_type_id : leg.trailer_type_id) || null,
            // Phase 1 per-leg trailer_id. Falls back to order-level when the
            // toggle is ON so legacy rows keep a synced trailer.
            trailer_id:
              (useSameAssetsUpd ? data.trailer_id : leg.trailer_id) || null,
            trip_type:
              (useSameTripTypeUpd ? null : (leg.trip_type || null)) as any,
            ship_from_street_address: leg.ship_from_street_address,
            ship_from_city: leg.ship_from_city,
            ship_from_province_state: leg.ship_from_province_state || null,
            ship_from_postal_zip_code: leg.ship_from_postal_zip_code || null,
            ship_from_country: leg.ship_from_country || null,
            ship_to_street_address: leg.ship_to_street_address,
            ship_to_city: leg.ship_to_city,
            ship_to_province_state: leg.ship_to_province_state || null,
            ship_to_postal_zip_code: leg.ship_to_postal_zip_code || null,
            ship_to_country: leg.ship_to_country || null,
            ship_from_location_id:
              ((leg as any).ship_from_location_id as string | null | undefined)
              ?? findLocationByAddress(locations, leg.ship_from_street_address, leg.ship_from_city)?.id
              ?? null,
            ship_to_location_id:
              ((leg as any).ship_to_location_id as string | null | undefined)
              ?? findLocationByAddress(locations, leg.ship_to_street_address, leg.ship_to_city)?.id
              ?? null,
            ready_time: formLocalToUtc(leg.ready_time),
            requested_delivery_time: formLocalToUtc(leg.requested_delivery_time),
            ready_time_end: formLocalToUtc(leg.ready_time_end || leg.ready_time),
            requested_delivery_time_end: formLocalToUtc(leg.requested_delivery_time_end || leg.requested_delivery_time),
            ...legAdvancedPayload(leg),
          };
          // Same rationale as the parent write above: only send changed columns.
          const legWrite = diffUpdate(legSnapshots.get(leg.id) ?? null, legUpdate);
          if (Object.keys(legWrite).length > 0) {
            const { error } = await supabase.from("legs").update(legWrite).eq("id", leg.id);
            if (error) throw error;
          }
        }

        // Auto-create trailer if trailer_id was set/changed
        const trailerIdChanged = data.trailer_id && data.trailer_id !== (originalOrder.trailer_id || "");
        if (trailerIdChanged) {
          await ensureTrailerExists({
            trailerId: data.trailer_id,
            trailerTypeId: data.trailer_type_id || originalOrder.trailer_type_id,
            shipFromCity: firstLeg.ship_from_city,
            tenantId,
          });
        }

        // Phase 3 dual-write: re-sync legs (per-leg stops/times/assignments)
        // and the active trip after the multi-leg parent + children are updated.
        await syncOrderLegsAndTrip(id);

        // Manual "Delivered" fan-out: a dispatcher setting the parent status
        // to delivered must leave every leg delivered too, otherwise the read
        // path (which caps the parent contribution at `confirmed` for
        // multi-leg orders) renders stale legs and downstream consumers
        // disagree with the order status.
        if (data.status === "delivered" && originalOrder.status !== "delivered") {
          await fanOutDeliveredToLegs({
            orderId: id,
            tenantId,
            changedBy: userEmail || "Unknown",
          });
          // Settle the trailer: without this the trailer stays `in_transit`
          // forever when an order is closed out anywhere but the driver app.
          await settleTrailerAfterOrderClose({
            orderId: id,
            trailerHumanId: data.trailer_id ?? originalOrder.trailer_id,
            tenantId,
            outcome: "delivered",
          });
        }

        // ── Change-log fan-out (parent + per-leg) ──
        // The single-leg branch returns from this mutation early, so the
        // change-log block at the bottom of `updateMutation` never runs for
        // multi-leg edits. Mirror it here, tagging per-leg field names with
        // a `leg_N_` prefix so they show up on the order timeline.
        try {
          const cl: { field_name: string; old_value: string | null; new_value: string | null }[] = [];
          const resolveName = (field: string, val: string | null): string => {
            if (!val) return "";
            if (field.endsWith("assigned_tractor_id")) {
              return tractors?.find((t) => t.id === val)?.name || val;
            }
            if (
              field.endsWith("assigned_operator_id") ||
              field.endsWith("assigned_operator_2_id")
            ) {
              return drivers?.find((d) => d.id === val)?.name || val;
            }
            return val;
          };
          const timeFieldNames = new Set([
            "ready_time",
            "requested_delivery_time",
            "ready_time_end",
            "requested_delivery_time_end",
          ]);
          const diffField = (prefix: string, field: string, oldRaw: any, newRaw: any) => {
            const oldVal = oldRaw == null ? "" : String(oldRaw);
            const newVal = newRaw == null ? "" : String(newRaw);
            if (timeFieldNames.has(field) && oldVal && newVal) {
              if (new Date(oldVal).getTime() === new Date(newVal).getTime()) return;
            } else if (oldVal === newVal) {
              return;
            }
            cl.push({
              field_name: `${prefix}${field}`,
              old_value: resolveName(field, oldVal || null),
              new_value: resolveName(field, newVal || null),
            });
          };
          // Parent-level fields
          diffField("", "status", originalOrder.status, parentUpdate.status);
          diffField("", "tender_id", (originalOrder as any).tender_id, parentUpdate.tender_id);
          diffField("", "trailer_id", originalOrder.trailer_id, parentUpdate.trailer_id);
          if (useSameAssetsUpd) {
            diffField("", "assigned_tractor_id", originalOrder.assigned_tractor_id, parentUpdate.assigned_tractor_id);
            diffField("", "assigned_operator_id", originalOrder.assigned_operator_id, parentUpdate.assigned_operator_id);
            diffField("", "assigned_operator_2_id", (originalOrder as any).assigned_operator_2_id, parentUpdate.assigned_operator_2_id);
            diffField("", "trailer_type_id", originalOrder.trailer_type_id, parentUpdate.trailer_type_id);
          }
          // Per-leg fields
          for (let i = 0; i < legs.length; i++) {
            const leg = legs[i];
            const snap = leg.id ? legSnapshots.get(leg.id) : null;
            if (!snap) continue;
            const seq = snap.leg_sequence ?? i + 1;
            const prefix = `leg_${seq}_`;
            const fromLoc = findLocationByAddress(locations, leg.ship_from_street_address, leg.ship_from_city);
            const toLoc = findLocationByAddress(locations, leg.ship_to_street_address, leg.ship_to_city);
            const newReady = formLocalToUtc(leg.ready_time);
            const newReadyEnd = formLocalToUtc(leg.ready_time_end || leg.ready_time);
            const newDelivery = formLocalToUtc(leg.requested_delivery_time);
            const newDeliveryEnd = formLocalToUtc(leg.requested_delivery_time_end || leg.requested_delivery_time);
            diffField(prefix, "ready_time", snap.ready_time, newReady);
            diffField(prefix, "ready_time_end", snap.ready_time_end, newReadyEnd);
            diffField(prefix, "requested_delivery_time", snap.requested_delivery_time, newDelivery);
            diffField(prefix, "requested_delivery_time_end", snap.requested_delivery_time_end, newDeliveryEnd);
            diffField(prefix, "ship_from_city", snap.ship_from_city, leg.ship_from_city);
            diffField(prefix, "ship_to_city", snap.ship_to_city, leg.ship_to_city);
            // Log the trip type each leg actually runs as, in both modes:
            // a leg's own value, else the parent's (legs store null when one
            // trip type applies to all legs).
            diffField(
              prefix,
              "trip_type",
              snap.trip_type || originalOrder.trip_type,
              useSameTripTypeUpd ? data.trip_type : (leg.trip_type || data.trip_type),
            );
            if (!useSameAssetsUpd) {
              diffField(prefix, "assigned_tractor_id", snap.assigned_tractor_id, leg.assigned_tractor_id || null);
              diffField(prefix, "assigned_operator_id", snap.assigned_operator_id, leg.assigned_operator_id || null);
              diffField(prefix, "assigned_operator_2_id", snap.assigned_operator_2_id, leg.assigned_operator_2_id || null);
              diffField(prefix, "trailer_type_id", snap.trailer_type_id, leg.trailer_type_id || null);
              diffField(prefix, "trailer_id", snap.trailer_id, leg.trailer_id || null);
            }
          }
          if (cl.length > 0) {
            await supabase.from("order_change_log").insert(
              cl.map((c) => ({
                order_id: id,
                changed_by: userEmail || "Unknown",
                ...(tenantId ? { tenant_id: tenantId } : {}),
                ...c,
              })),
            );
          }
        } catch (logErr) {
          console.warn("[multi-leg change-log] failed to insert change log entries:", logErr);
        }

        // ── Mirror per-leg EAPI trips into public.trips ──
        // Runs after `syncOrderLegsAndTrip` so we know each leg row exists in
        // public.legs with the freshly persisted leg_sequence.
        if (promoteMultiLeg && createdLegTripIds.length > 0) {
          for (const created of createdLegTripIds) {
            const legRow = await fetchLegRow(id, created.legSequence);
            if (!legRow) {
              console.warn(
                `[multi-leg promote] leg row missing for seq ${created.legSequence}; skipping mirror insert`,
              );
              continue;
            }
            await insertActiveLegTrip({
              legId: legRow.id,
              tenantId: legRow.tenant_id,
              eapiTripId: created.eapiTripId,
            });
          }
        }

        // Local writes stand; surface untracked trip ids instead of a clean save.
        if (unlinkedTrips.length > 0) {
          throw new Error(`Order saved, but external trip tracking failed. ${describeUnlinkedTrips(unlinkedTrips)}`);
        }

        return;
      }

      // --- Single-leg update path ---
      // Hydrate originals as wall-clock strings in the corresponding stop's local tz,
      // matching the format the edit dialog feeds back into the form.
      const fromLocOrig = findLocationByAddress(
        locations,
        originalOrder.ship_from_street_address,
        originalOrder.ship_from_city,
      );
      const toLocOrig = findLocationByAddress(
        locations,
        originalOrder.ship_to_street_address,
        originalOrder.ship_to_city,
      );
      const fromLocNew = findLocationByAddress(
        locations,
        data.ship_from_street_address,
        data.ship_from_city,
      );
      const toLocNew = findLocationByAddress(
        locations,
        data.ship_to_street_address,
        data.ship_to_city,
      );
      // Same Central hydration as the edit form (utcToFormLocal), so an
      // untouched field compares equal and doesn't fire a phantom change.
      const originalReadyTimeFormatted = utcToFormLocal(originalOrder.ready_time);
      const originalDeliveryTimeFormatted = utcToFormLocal(originalOrder.requested_delivery_time);
      const originalReadyEndFormatted = utcToFormLocal(originalOrder.ready_time_end);
      const originalDeliveryEndFormatted = utcToFormLocal(originalOrder.requested_delivery_time_end);

      // Status is taken verbatim from the caller. Promotion to "confirmed"
      // only happens when dispatch explicitly chooses it (e.g. via the
      // Confirm Order button or the status field) — never implicitly because
      // a trailer was assigned.
      const trailerIdChanged =
        !!data.trailer_id && data.trailer_id !== (originalOrder.trailer_id || "");
      const effectiveStatus = statusForWrite;

      // ── Booked → Confirmed promotion: create the external trip BEFORE
      // persisting the local update (sync-then-persist). Only runs for orders
      // that don't already have an EAPI trip and aren't in a no-EAPI state.
      let promotedEapiTripId: string | null = null;
      const isPromotion =
        !originalOrder.eapi_trip_id &&
        effectiveStatus === "confirmed" &&
        data.status !== "canceled" &&
        !newStateSkipsEapi;
      if (isPromotion) {
        const client = await waitForEapiClient();
        if (!client) {
          throw new Error(
            `EAPI authentication did not become ready within ${EAPI_READY_WAIT_MS / 1000} seconds`,
          );
        }
        const promotionTractor =
          tractors?.find((t) => t.id === (data.assigned_tractor_id || originalOrder.assigned_tractor_id)) ?? null;
        const promoFromLoc = findLocationByAddress(
          locations,
          data.ship_from_street_address || originalOrder.ship_from_street_address,
          data.ship_from_city || originalOrder.ship_from_city,
        );
        const promoToLoc = findLocationByAddress(
          locations,
          data.ship_to_street_address || originalOrder.ship_to_street_address,
          data.ship_to_city || originalOrder.ship_to_city,
        );
        const promoReadyUtc = data.ready_time
          ? formLocalToUtc(data.ready_time) ?? originalOrder.ready_time
          : originalOrder.ready_time;
        const promoDeliveryUtc = data.requested_delivery_time
          ? formLocalToUtc(data.requested_delivery_time) ?? originalOrder.requested_delivery_time
          : originalOrder.requested_delivery_time;
        const promoReadyEndUtc = data.ready_time_end
          ? formLocalToUtc(data.ready_time_end) ?? promoReadyUtc
          : (originalOrder.ready_time_end ?? promoReadyUtc);
        const promoDeliveryEndUtc = data.requested_delivery_time_end
          ? formLocalToUtc(data.requested_delivery_time_end) ?? promoDeliveryUtc
          : (originalOrder.requested_delivery_time_end ?? promoDeliveryUtc);
        const tripBody = mapOrderToCreateTrip(
          {
            ...originalOrder,
            ...data,
            ready_time: promoReadyUtc,
            requested_delivery_time: promoDeliveryUtc,
            ready_time_end: promoReadyEndUtc,
            requested_delivery_time_end: promoDeliveryEndUtc,
            unique_id: originalOrder.unique_id,
            // Preserve FK location IDs when the form value is empty/blank
            // (an empty string from a not-yet-hydrated form would otherwise
            // overwrite a valid FK and drop origin/destination latlng).
            ship_from_location_id:
              ((data as { ship_from_location_id?: string | null }).ship_from_location_id || null)
              ?? originalOrder.ship_from_location_id
              ?? null,
            ship_to_location_id:
              ((data as { ship_to_location_id?: string | null }).ship_to_location_id || null)
              ?? originalOrder.ship_to_location_id
              ?? null,
          } as unknown as Order,
          promotionTractor,
          newTrailerTypeInfo,
          locations,
          await buildCustomFieldsForOrder(client, eapiEnvironmentRef.current, {
            customerShortCode: originalOrder.billing_party?.short_code,
            tripType: (data as any).trip_type ?? (originalOrder as any).trip_type,
            trailerId: data.trailer_id ?? originalOrder.trailer_id,
            operator1Email: drivers?.find(
              (d) => d.id === (data.assigned_operator_id ?? originalOrder.assigned_operator_id),
            )?.email,
            operator2Email: drivers?.find(
              (d) => d.id === ((data as any).assigned_operator_2_id ?? (originalOrder as any).assigned_operator_2_id),
            )?.email,
          }),
        );
        console.log("EAPI createTrip payload (booked → confirmed):", tripBody);
        // A one-leg order is the n = 1 case of the same fan-out the multi-leg
        // path uses: same create-and-verify rules, same rollback contract.
        const createdSingle = await createTripsForLegs({
          requests: [
            {
              legSequence: 1,
              tripBody,
              label: "single-leg confirm",
              assert: {
                flow: "single-leg confirm",
                uniqueId: originalOrder.unique_id,
              },
            },
          ],
          create: (req) =>
            createEapiTripChecked(client, req.tripBody as EapiCreateTripBody, {
              label: req.label,
              assert: req.assert as any,
            }),
          cancel: (tripId, reason) => client.cancelTrip(tripId, reason),
          rollbackReason: "Single-leg confirmation rollback",
          logLabel: "single-leg promote",
        });
        promotedEapiTripId = createdSingle[0]?.eapiTripId ?? null;
        console.log("EAPI trip created on confirmation:", promotedEapiTripId);
      }

      const updateData: Record<string, unknown> = {
        status: effectiveStatus,
        ship_from_street_address: data.ship_from_street_address,
        ship_from_city: data.ship_from_city,
        ship_from_province_state: data.ship_from_province_state || null,
        ship_from_postal_zip_code: data.ship_from_postal_zip_code || null,
        ship_from_country: data.ship_from_country || null,
        ship_to_street_address: data.ship_to_street_address,
        ship_to_city: data.ship_to_city,
        ship_to_province_state: data.ship_to_province_state || null,
        ship_to_postal_zip_code: data.ship_to_postal_zip_code || null,
        ship_to_country: data.ship_to_country || null,
        // Repoint FK location IDs to match the (possibly edited) address.
        // Without this the row's denormalized street/city would drift from
        // its `*_location_id` and the Edit form (which binds the picker to
        // the FK) would keep displaying the pre-edit short_code.
        ship_from_location_id:
          ((data as { ship_from_location_id?: string | null }).ship_from_location_id || null)
          ?? findLocationByAddress(locations, data.ship_from_street_address, data.ship_from_city)?.id
          ?? null,
        ship_to_location_id:
          ((data as { ship_to_location_id?: string | null }).ship_to_location_id || null)
          ?? findLocationByAddress(locations, data.ship_to_street_address, data.ship_to_city)?.id
          ?? null,
        billing_party_id: data.billing_party_id,
        trailer_id: data.trailer_id || null,
        trailer_type_id: data.trailer_type_id || null,
        tender_id: data.tender_id || null,
        trip_type: data.trip_type || null,
        assigned_tractor_id: data.assigned_tractor_id || null,
        assigned_operator_id: data.assigned_operator_id || null,
        assigned_operator_2_id: data.assigned_operator_2_id || null,
        ready_time: data.ready_time === originalReadyTimeFormatted
          ? originalOrder.ready_time
          : formLocalToUtc(data.ready_time),
        requested_delivery_time: data.requested_delivery_time === originalDeliveryTimeFormatted
          ? originalOrder.requested_delivery_time
          : formLocalToUtc(data.requested_delivery_time),
        ready_time_end:
          (data.ready_time_end || data.ready_time) ===
          (originalReadyEndFormatted || originalReadyTimeFormatted)
            ? (originalOrder.ready_time_end ?? originalOrder.ready_time)
            : formLocalToUtc(data.ready_time_end || data.ready_time),
        requested_delivery_time_end:
          (data.requested_delivery_time_end || data.requested_delivery_time) ===
          (originalDeliveryEndFormatted || originalDeliveryTimeFormatted)
            ? (originalOrder.requested_delivery_time_end ?? originalOrder.requested_delivery_time)
            : formLocalToUtc(data.requested_delivery_time_end || data.requested_delivery_time),
        ...orderAdvancedPayload(data),
      };

      // Add cancellation fields when status is canceled
      if (effectiveStatus === "canceled") {
        updateData.canceled_at = new Date().toISOString();
        updateData.cancellation_reason = data.cancellation_reason || null;
        updateData.cancellation_notes = data.cancellation_notes || null;
      }

      // Manual status promotion: stamp write-once actual timestamps so the
      // edit-order form mirrors the driver-app / EAPI-poller behavior. Only
      // fill nulls — preserve any prior driver/EAPI-provided values.
      const manualStatusChanged = effectiveStatus !== originalOrder.status;
      const manualNowIso = new Date().toISOString();
      const manualStampedDeparture =
        manualStatusChanged &&
        (effectiveStatus === "in_transit" || effectiveStatus === "delivered") &&
        !(originalOrder as any).actual_departure_time;
      const manualStampedArrival =
        manualStatusChanged &&
        effectiveStatus === "delivered" &&
        !(originalOrder as any).actual_arrival_time;
      if (manualStampedDeparture) {
        updateData.actual_departure_time = manualNowIso;
      }
      if (manualStampedArrival) {
        updateData.actual_arrival_time = manualNowIso;
      }

      // Trip pivot / clear / promote: trip ids are written to leg 1's `trips`
      // row after the save (below), never to the deprecated
      // `orders.eapi_trip_id` column. Only reset the cached ETA here.
      if ((data as any)._newEapiTripId || (data as any)._clearEapiTripId || promotedEapiTripId) {
        updateData.eapi_eta = null;
      }

      // Capture who promoted the order from booked → confirmed (set once).
      if (
        effectiveStatus === "confirmed" &&
        originalOrder.status !== "confirmed" &&
        !(originalOrder as any).confirmed_by_user_id
      ) {
        const { data: authData } = await supabase.auth.getUser();
        if (authData?.user?.id) {
          updateData.confirmed_by_user_id = authData.user.id;
          updateData.confirmed_at = new Date().toISOString();
        }
      }

      // Write only columns whose value actually changed — untouched fields
      // must not re-trigger server-side locks (e.g. the post-in_transit
      // tractor freeze) or other row-level rules. `updateData` is still used
      // below for change-log diffing.
      const updateWrite = diffUpdate(originalOrder as any, updateData);

      // Sanctioned cancel path: when transitioning into 'canceled', perform the
      // cancel via the atomic cancel_order_locally RPC so the trigger opt-in
      // and the status update share a single connection/transaction. A
      // follow-up UPDATE handles any non-status field edits the form may have
      // included alongside the cancel.
      if (
        effectiveStatus === "canceled" &&
        originalOrder.status !== "canceled"
      ) {
        const { error: cancelErr } = await supabase.rpc("cancel_order_locally", {
          _order_id: id,
          _reason: (updateData.cancellation_reason as string | null) ?? null,
          _notes: (updateData.cancellation_notes as string | null) ?? null,
        });
        if (cancelErr) throw cancelErr;

        // Strip fields the RPC already wrote so we don't redundantly UPDATE them.
        const {
          status: _omitStatus,
          canceled_at: _omitCanceledAt,
          cancellation_reason: _omitReason,
          cancellation_notes: _omitNotes,
          ...remainingUpdate
        } = updateWrite;

        if (Object.keys(remainingUpdate).length > 0) {
          const { error: tailErr } = await supabase
            .from("orders")
            .update(remainingUpdate)
            .eq("id", id);
          if (tailErr) throw tailErr;
        }

        // A canceled order releases its trailer too — otherwise it stays
        // pinned to `in_transit` and drops out of the yard workflows.
        await settleTrailerAfterOrderClose({
          orderId: id,
          trailerHumanId: data.trailer_id ?? originalOrder.trailer_id,
          tenantId,
          outcome: "canceled",
        });
      } else if (Object.keys(updateWrite).length > 0) {
        const { error } = await supabase.from("orders").update(updateWrite).eq("id", id);
        if (error) throw error;
      }

      // Re-sync the leg rows only. Trips are never mirrored from
      // `orders.eapi_trip_id` — that copy reverted a regenerate on
      // AOP-WENP-03315. Order-level pivots/clears write leg 1's trip directly.
      await syncOrderToLegs(id);
      {
        const newTripId = (data as any)._newEapiTripId as string | undefined;
        const clearTrip = !!(data as any)._clearEapiTripId;
        if (newTripId || clearTrip) {
          const legRow = await fetchLegRow(id, 1);
          if (legRow) {
            const active = (await listActiveLegTripsForOrder(id)).filter(
              (t) => t.leg_id === legRow.id && t.eapi_trip_id !== newTripId,
            );
            await Promise.all(
              active.map((t) =>
                supersedeLegTrip(t.trip_row_id, clearTrip ? "no_eapi_switch" : "tractor_pivot"),
              ),
            );
            if (newTripId) {
              const already = (await listActiveLegTripsForOrder(id)).some(
                (t) => t.leg_id === legRow.id && t.eapi_trip_id === newTripId,
              );
              if (!already) {
                const res = await insertActiveLegTrip({
                  legId: legRow.id,
                  tenantId: legRow.tenant_id,
                  eapiTripId: newTripId,
                });
                if (!res.ok) {
                  toast.error(
                    `New external trip ${newTripId} was created but could not be saved to the stop: ${res.error}`,
                  );
                }
              }
            }
          }
        }
      }

      // Record the freshly created trip against leg 1 directly, exactly as the
      // multi-leg path does for each of its legs. `sync_order_trip` above will
      // already have mirrored it from `orders.eapi_trip_id`; this makes the
      // legs+trips row the thing we wrote rather than a side effect, and is a
      // no-op when the mirror got there first.
      if (promotedEapiTripId) {
        const legRow = await fetchLegRow(id, 1);
        if (legRow) {
          const { data: existingTrip } = await supabase
            .from("trips" as any)
            .select("id")
            .eq("leg_id", legRow.id)
            .eq("eapi_trip_id", promotedEapiTripId)
            .eq("is_active", true)
            .maybeSingle();
          if (!existingTrip) {
            await insertActiveLegTrip({
              legId: legRow.id,
              tenantId: legRow.tenant_id,
              eapiTripId: promotedEapiTripId,
            });
          }
        } else {
          console.warn("[single-leg promote] leg row missing; trip mirrored on the order only");
        }
      }

      // Advanced leg-only columns (load status / temperature) have no `orders`
      // counterpart, so write them onto the mirrored leg after the re-sync.
      // The trailer is written explicitly: `legs.trailer_id` is authoritative
      // and the DB sync never copies it.
      await supabase
        .from("legs")
        .update({ ...legAdvancedPayload(data), trailer_id: data.trailer_id || null } as any)
        .eq("order_id", id)
        .eq("leg_sequence", 1);

      // Manual "Delivered" fan-out (single-leg path): keep the leg row's
      // driver_status in lockstep with a manually delivered parent order.
      if (effectiveStatus === "delivered" && originalOrder.status !== "delivered") {
        await fanOutDeliveredToLegs({
          orderId: id,
          tenantId,
          changedBy: userEmail || "Unknown",
        });
        await settleTrailerAfterOrderClose({
          orderId: id,
          trailerHumanId: data.trailer_id ?? originalOrder.trailer_id,
          tenantId,
          outcome: "delivered",
        });
      }

      // Auto-create trailer if trailer_id was set/changed
      if (trailerIdChanged) {
        await ensureTrailerExists({
          trailerId: data.trailer_id,
          trailerTypeId: data.trailer_type_id || originalOrder.trailer_type_id,
          shipFromCity: data.ship_from_city,
          tenantId,
        });
      }

      // Track changes
      const changes: { field_name: string; old_value: string | null; new_value: string | null }[] = [];
      const fieldsToTrack = [
        "status", "ship_from_city", "ship_to_city", "ready_time",
        "requested_delivery_time", "trailer_id", "trailer_type_id",
        "tender_id", "assigned_tractor_id", "assigned_operator_id", "assigned_operator_2_id", "trip_type",
      ];
      const timeFields = ["ready_time", "requested_delivery_time"];

      const resolveName = (field: string, val: string | null): string => {
        if (!val) return "";
        if (field === "assigned_tractor_id") return tractors?.find(t => t.id === val)?.name || val;
        if (field === "assigned_operator_id" || field === "assigned_operator_2_id") return drivers?.find(d => d.id === val)?.name || val;
        return val;
      };

      fieldsToTrack.forEach((field) => {
        if (!(field in updateData)) return;
        let oldVal = String((originalOrder as any)[field] || "");
        let newVal = String((updateData as any)[field] || "");
        if (timeFields.includes(field) && oldVal && newVal) {
          if (new Date(oldVal).getTime() === new Date(newVal).getTime()) return;
        } else if (oldVal === newVal) return;
        changes.push({
          field_name: field,
          old_value: resolveName(field, oldVal || null),
          new_value: resolveName(field, newVal || null),
        });
      });

      if (changes.length > 0) {
        const changeLogEntries = changes.map((change) => ({
          order_id: id,
          changed_by: userEmail || "Unknown",
          ...(tenantId ? { tenant_id: tenantId } : {}),
          ...change,
        }));
        await supabase.from("order_change_log").insert(changeLogEntries);
      }

      // Audit the manual actual_departure_time / actual_arrival_time stamps
      // we wrote above so the order history reflects WHO promoted the order
      // (not just "System (EAPI)").
      const tsAuditRows: Array<{
        field_name: string;
        old_value: string | null;
        new_value: string | null;
      }> = [];
      if (manualStampedDeparture) {
        tsAuditRows.push({
          field_name: "actual_departure_time",
          old_value: null,
          new_value: manualNowIso,
        });
      }
      if (manualStampedArrival) {
        tsAuditRows.push({
          field_name: "actual_arrival_time",
          old_value: null,
          new_value: manualNowIso,
        });
      }
      if (tsAuditRows.length > 0) {
        await supabase.from("order_change_log").insert(
          tsAuditRows.map((r) => ({
            order_id: id,
            changed_by: userEmail || "Unknown",
            ...(tenantId ? { tenant_id: tenantId } : {}),
            ...r,
          })),
        );
      }

      // Best-effort customer email notification for manual status promotions.
      // Mirrors the driver-app + EAPI-poller paths: only fires on root orders
      // transitioning to in_transit / delivered. All eligibility (AURY skip,
      // missing email, parent-only, de-dup) is enforced inside send-email.
      if (
        !historicalEdit &&
        manualStatusChanged &&
        (effectiveStatus === "in_transit" ||
          effectiveStatus === "delivered" ||
          effectiveStatus === "canceled") &&
        !((originalOrder as any).parent_order_id)
      ) {
        void notifyOrderStatus({
          orderId: id,
          newStatus: effectiveStatus as
            | "in_transit"
            | "delivered"
            | "canceled",
          initiator: "dispatcher-ui",
          tenantSlug: activeTenant,
          // Pre-edit status. Only consumed by send-email's
          // "cancelled from booked" gate; ignored for other transitions.
          previousStatus: originalOrder.status ?? null,
        });
      }

      // Best-effort trailer ↔ BoL S3 reconciliation. Runs AFTER the local
      // update succeeds and never blocks/aborts the update.
      const newTrailerId = (data.trailer_id ?? "").trim();
      const oldTrailerId = (originalOrder.trailer_id ?? "").trim();
      const trailerChanged = newTrailerId !== oldTrailerId;

      if (trailerChanged) {
        // 1. If a previous trailer was attached, delete its synced BoLs from
        //    S3 first so we don't leave orphaned objects in the bucket.
        if (oldTrailerId !== "" && tenantId) {
          // Resolve the previous trailer row id (text → uuid) within tenant.
          const { data: prevTrailerRow } = await supabase
            .from("trailers")
            .select("id")
            .eq("trailer_id", oldTrailerId)
            .eq("tenant_id", tenantId)
            .maybeSingle();
          if (prevTrailerRow?.id) {
            void removeTrailerBolFromOrder({
              orderId: id,
              orderUniqueId: originalOrder.unique_id,
              previousTrailerRowId: prevTrailerRow.id,
              tenantId,
              tenantSlug: activeTenant,
            });
          }
        }

        // 2. If a new trailer is now attached, push its check-in BoL (if any)
        //    to S3 tagged with this order's unique_id.
        if (newTrailerId !== "") {
          void syncTrailerBol({
            direction: "order-gains-trailer",
            orderId: id,
            tenantSlug: activeTenant,
          }).then((r) => {
            if (r.synced > 0) {
              queryClient.invalidateQueries({ queryKey: ["order-detail", id] });
              queryClient.invalidateQueries({ queryKey: ["bol-count", id] });
              queryClient.invalidateQueries({ queryKey: ["bol-images", id] });
              queryClient.invalidateQueries({ queryKey: ["bol-images-count", id] });
              queryClient.invalidateQueries({ queryKey: ["bol-extractions", id] });
            }
          });
        }
      }

      // Cancellation cleanup: when status transitions to canceled, drop all
      // BoLs from S3 + DB. S3 metadata is handled by the DB trigger.
      if (
        effectiveStatus === "canceled" &&
        originalOrder.status !== "canceled" &&
        tenantId &&
        activeTenant
      ) {
        void removeOrderBolsOnCancel({
          orderId: id,
          orderUniqueId: originalOrder.unique_id,
          tenantId,
          tenantSlug: activeTenant,
        }).then(() => {
          queryClient.invalidateQueries({ queryKey: ["order-detail", id] });
          queryClient.invalidateQueries({ queryKey: ["bol-count", id] });
          queryClient.invalidateQueries({ queryKey: ["bol-images", id] });
          queryClient.invalidateQueries({ queryKey: ["bol-images-count", id] });
          queryClient.invalidateQueries({ queryKey: ["bol-extractions", id] });
        });
      }
    },
    onSuccess: async (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
      // Booked-truck lookup reads orders + legs; refresh it so a pivot
      // shows up immediately in other orders' tractor dropdowns.
      queryClient.invalidateQueries({ queryKey: ["tractor-conflict-ids"] });
      queryClient.invalidateQueries({ queryKey: ["trailers"] });
      // Traceable record of an admin confirming without required billing
      // documentation. Best-effort — never fails the update.
      if (variables?.data?._docsOverride && tenantId) {
        const evt = buildConfirmDocsOverrideEvent({
          orderId: variables.id,
          tenantId,
          eapiTripId: (variables.originalOrder as any)?.eapi_trip_id ?? null,
          ack: variables.data._docsOverride,
          previousStatus: variables.originalOrder?.status ?? null,
        });
        if (evt) {
          void recordChangeEvents([evt]).then((r) => {
            if (r.error) console.warn("[useOrderMutations] confirm-docs override log failed", r.error);
          });
        }
      }
      // Traceable record of the operator turning "Pending BOL" on.
      if (
        tenantId &&
        pendingBolValue(variables?.data as any) &&
        (variables?.originalOrder as any)?.pending_bol !== true
      ) {
        void recordChangeEvents([
          {
            orderId: variables.id,
            tenantId,
            eapiTripId: (variables.originalOrder as any)?.eapi_trip_id ?? null,
            eventType: "pending_bol_enabled",
            reason: "documentation_override",
            previousValue: { pending_bol: false },
            newValue: { pending_bol: true },
          },
        ]).then((r) => {
          if (r.error) console.warn("[useOrderMutations] pending-bol log failed", r.error);
        });
      }
      // Ensure the open Order Details page reflects edits immediately,
      // including trailer changes that drive BoL sync side effects.
      queryClient.invalidateQueries({ queryKey: ["order-detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["order-children", variables.id] });
      // The Order Detail page and the detail sheet read per-leg assets
      // (tractor / trailer / trip type) from public.legs under their own
      // cache keys. Without these, the Confirm gate keeps evaluating the
      // pre-edit legs — e.g. a tractor cleared in Edit still looks assigned,
      // so Confirm Order renders enabled and only fails at the write-path
      // preflight ("Tractor Missing"). Keep these in lock-step with the keys
      // used in src/pages/OrderDetail.tsx / OrderDetailSheet.tsx.
      queryClient.invalidateQueries({ queryKey: ["order-children-legs", variables.id] });
      // Catch-all: the edit dialog and detail surfaces hold several more
      // order-shaped caches (order-child-legs, order-single-leg-advanced,
      // order-edit-leg-count, active-leg-trips, ...) that otherwise keep
      // rendering pre-save values until a page refresh.
      invalidateOrderQueries(queryClient);

      queryClient.invalidateQueries({ queryKey: ["bol-count", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["bol-images", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["bol-images-count", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["tender-count", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["tender-images", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["tender-images-count", variables.id] });
      toast.success("Order updated successfully");
      onSuccess();
    },
    onError: (error: Error) => {
      const docs = parseConfirmDocsError(error.message);
      if (docs) {
        toast.error(formatMissingDocs(docs) || "Missing billing documentation to confirm this order.");
        return;
      }
      // Map sentinel errors to user-friendly toasts. Modal-level handlers in
      // OrderEditDialog catch these by message and show a richer prompt; the
      // toast is the fallback for non-dialog callers (inline edits, etc.).
      if (error.message === "CONFIRM_REQUIRES_TRUCK_TRAILER") {
        toast.error("To confirm an order, you must supply a truck and trailer");
        return;
      }
      if (error.message === "CONFIRMED_REQUIRES_TRUCK_TRAILER") {
        toast.error("This order is confirmed and cannot have its truck or trailer removed");
        return;
      }
      if (error.message?.startsWith("CONFIRM_REQUIRES_TRAILER_ON_SITE")) {
        const code = error.message.split("::")[1] || "";
        toast.error(
          code
            ? `Trailer ${code} is not checked in on site. Check it in from the Yard before confirming.`
            : "The assigned trailer must be checked in on site before this order can be confirmed.",
        );
        return;
      }
      // Post-in_transit tractor lock (DB trigger). Surface a readable
      // explanation instead of the raw Postgres message.
      if (/Cannot change tractor on order/i.test(error.message || "")) {
        toast.error(
          "The truck can't be changed once an order is in transit. Other fields can still be edited.",
        );
        return;
      }
      toast.error(getTractorDoubleBookingMessage(error) ?? error.message);
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: async (trips: ParsedTrip[]) => {
      await ensureTenantContext();
      const singles = trips.filter((t) => t.kind === "single") as Extract<ParsedTrip, { kind: "single" }>[];
      const multis = trips.filter((t) => t.kind === "multi") as Extract<ParsedTrip, { kind: "multi" }>[];

      if (trips.length > 0 && !locations) {
        // Same rationale as createMutation: avoid silently persisting null
        // location FKs. Parent `orders` rows have no DB trigger backfilling
        // them (only `legs` does), so failing fast here is the only guard.
        throw new Error(
          "Locations are still loading. Please wait a moment and re-run the import.",
        );
      }

      // -- Single-leg trips: keep the existing batched RPC. --
      const insertRows = singles.map(({ row }) => ({
        status: "booked" as OrderStatus,
        ship_from_street_address: row.ship_from_street_address,
        ship_from_city: row.ship_from_city,
        ship_from_province_state: row.ship_from_province_state || null,
        ship_from_postal_zip_code: row.ship_from_postal_zip_code || null,
        ship_from_country: row.ship_from_country || null,
        ship_to_street_address: row.ship_to_street_address,
        ship_to_city: row.ship_to_city,
        ship_to_province_state: row.ship_to_province_state || null,
        ship_to_postal_zip_code: row.ship_to_postal_zip_code || null,
        ship_to_country: row.ship_to_country || null,
        ship_from_location_id:
          findLocationByAddress(locations, row.ship_from_street_address, row.ship_from_city)?.id ?? null,
        ship_to_location_id:
          findLocationByAddress(locations, row.ship_to_street_address, row.ship_to_city)?.id ?? null,
        ready_time: row.ready_time,
        requested_delivery_time: row.requested_delivery_time,
        ready_time_end: row.ready_time_end || row.ready_time,
        requested_delivery_time_end: row.requested_delivery_time_end || row.requested_delivery_time,
        billing_party_id: row.billing_party_id || null,
        trailer_id: row.trailer_id || null,
        trailer_type_id: row.trailer_type_id || null,
        tender_id: row.tender_id || null,
        trip_type: (row.trip_type || null) as any,
        assigned_tractor_id: row.assigned_tractor_id || null,
        assigned_operator_id: row.assigned_operator_id || null,
        assigned_operator_2_id: row.assigned_operator_2_id || null,
        unique_id: "",
        created_by: userEmail || null,
        ...(tenantId ? { tenant_id: tenantId } : {}),
      }));
      if (insertRows.length > 0) {
        const { error } = await supabase.rpc("insert_orders_batch", { _orders: insertRows });
        if (error) throw error;
        // Auto-provision trailers for each unique trailer_id in the batch.
        const seen = new Set<string>();
        for (const row of insertRows) {
          const tid = (row.trailer_id || "").trim();
          if (!tid || seen.has(tid)) continue;
          seen.add(tid);
          await safeEnsureTrailerExists({
            trailerId: tid,
            trailerTypeId: row.trailer_type_id ?? null,
            shipFromCity: row.ship_from_city ?? null,
            tenantId,
          });
        }
      }

      // -- Multi-leg trips: insert parent + legs per trip. No EAPI calls. --
      let multiTripCount = 0;
      let multiLegCount = 0;
      const failures: string[] = [];
      for (const trip of multis) {
        const { parent: p, legs } = trip;
        try {
          if (!tenantId) throw new Error("No active tenant");
          const parentData = {
            status: "booked" as OrderStatus,
            billing_party_id: p.billing_party_id || null,
            assigned_tractor_id: p.assigned_tractor_id || null,
            assigned_operator_id: p.assigned_operator_id || null,
            assigned_operator_2_id: p.assigned_operator_2_id || null,
            trailer_id: p.trailer_id || null,
            trailer_type_id: p.trailer_type_id || null,
            tender_id: p.tender_id || null,
            trip_type: (p.trip_type || null) as any,
            ship_from_street_address: p.ship_from_street_address,
            ship_from_city: p.ship_from_city,
            ship_from_province_state: p.ship_from_province_state || null,
            ship_from_postal_zip_code: p.ship_from_postal_zip_code || null,
            ship_from_country: p.ship_from_country || null,
            ship_to_street_address: p.ship_to_street_address,
            ship_to_city: p.ship_to_city,
            ship_to_province_state: p.ship_to_province_state || null,
            ship_to_postal_zip_code: p.ship_to_postal_zip_code || null,
            ship_to_country: p.ship_to_country || null,
            // Parent mirrors leg 1's pickup and the last leg's drop, same as
            // the form path in createMutation.
            ship_from_location_id:
              findLocationByAddress(locations, legs[0].ship_from_street_address, legs[0].ship_from_city)?.id ?? null,
            ship_to_location_id:
              findLocationByAddress(
                locations,
                legs[legs.length - 1].ship_to_street_address,
                legs[legs.length - 1].ship_to_city,
              )?.id ?? null,
            ready_time: p.ready_time,
            requested_delivery_time: p.requested_delivery_time,
            ready_time_end: p.ready_time_end || p.ready_time,
            requested_delivery_time_end: p.requested_delivery_time_end || p.requested_delivery_time,
            parent_order_id: null,
            leg_sequence: 0,
            unique_id: "",
            created_by: userEmail || null,
            tenant_id: tenantId,
          };
          const { data: parentId, error: parentError } = await supabase.rpc("insert_order", { _data: parentData });
          if (parentError) throw parentError;

          // Auto-provision a trailers row if the parent has a trailer_id.
          if (p.trailer_id) {
            await safeEnsureTrailerExists({
              trailerId: p.trailer_id,
              trailerTypeId: p.trailer_type_id ?? null,
              shipFromCity: p.ship_from_city,
              tenantId,
            });
          }

          const legRows = legs.map((leg) => ({
            order_id: parentId as string,
            tenant_id: tenantId,
            leg_sequence: leg.leg_sequence,
            assigned_tractor_id: leg.assigned_tractor_id || null,
            assigned_operator_id: leg.assigned_operator_id || null,
            assigned_operator_2_id: leg.assigned_operator_2_id || null,
            trailer_type_id: leg.trailer_type_id || null,
            // Per-leg trailer_id from the CSV row, falling back to the
            // parent trip's trailer_id when the leg cell is empty.
            trailer_id: leg.trailer_id || p.trailer_id || null,
            ship_from_street_address: leg.ship_from_street_address,
            ship_from_city: leg.ship_from_city,
            ship_from_province_state: leg.ship_from_province_state || null,
            ship_from_postal_zip_code: leg.ship_from_postal_zip_code || null,
            ship_from_country: leg.ship_from_country || null,
            ship_to_street_address: leg.ship_to_street_address,
            ship_to_city: leg.ship_to_city,
            ship_to_province_state: leg.ship_to_province_state || null,
            ship_to_postal_zip_code: leg.ship_to_postal_zip_code || null,
            ship_to_country: leg.ship_to_country || null,
            ship_from_location_id:
              (leg as any).ship_from_location_id ||
              findLocationByAddress(locations, leg.ship_from_street_address, leg.ship_from_city)?.id ||
              null,
            ship_to_location_id:
              (leg as any).ship_to_location_id ||
              findLocationByAddress(locations, leg.ship_to_street_address, leg.ship_to_city)?.id ||
              null,
            ready_time: leg.ready_time,
            requested_delivery_time: leg.requested_delivery_time,
            ready_time_end: leg.ready_time_end || leg.ready_time,
            requested_delivery_time_end: leg.requested_delivery_time_end || leg.requested_delivery_time,
          }));
          const { error: legsError } = await supabase.from("legs").insert(legRows);
          if (legsError) throw legsError;
          multiTripCount += 1;
          multiLegCount += legs.length;
        } catch (e: any) {
          failures.push(e?.message || String(e));
        }
      }

      return {
        singleCount: insertRows.length,
        multiTripCount,
        multiLegCount,
        failures,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
      // Booked-truck lookup reads orders + legs; refresh it so a pivot
      // shows up immediately in other orders' tractor dropdowns.
      queryClient.invalidateQueries({ queryKey: ["tractor-conflict-ids"] });
      const totalTrips = result.singleCount + result.multiTripCount;
      const legNote = result.multiTripCount > 0 ? ` (${result.multiLegCount} legs across ${result.multiTripCount} multi-leg trip${result.multiTripCount !== 1 ? "s" : ""})` : "";
      toast.success(`Imported ${totalTrips} trip${totalTrips !== 1 ? "s" : ""}${legNote}`);
      if (result.failures.length > 0) {
        toast.error(`${result.failures.length} multi-leg trip(s) failed: ${result.failures[0]}`);
      }
      onSuccess();
    },
    onError: (error: Error) => {
      const docs = parseConfirmDocsError(error.message);
      if (docs) {
        toast.error(formatMissingDocs(docs) || "Missing billing documentation to confirm this order.");
        return;
      }
      toast.error(getTractorDoubleBookingMessage(error) ?? error.message);
    },
  });

  return { createMutation, updateMutation, bulkCreateMutation };
}
